/**
 * Office.js PowerPoint colour operations for Colour Tools.
 *
 * Key design decisions:
 * - Font colours are replaced at paragraph level (not shape level) because
 *   textRange.font.color returns null when paragraphs have different colours.
 * - fill.type must be "solid" before reading foregroundColor to avoid
 *   a runtime error on gradient/picture fills.
 * - All operations are silent-fail: individual shape errors are caught so
 *   a single bad shape doesn't abort the entire batch.
 */

import { unzipSync, strFromU8, type Unzipped } from "fflate";
import { logError, logEvent, scrubMessage } from "../../../../../telemetry";
import { shapeTypeOf, supportsTextFrame } from "../../../utils/shapeTypes";
import {
  normalizeHex,
  isNearWhite,
  pickContrastText,
  findNearestThemeColor,
  findNearestThemeFillColor,
  getAccentHexes,
} from "../../../utils/colourUtils";
import type {
  ThemeColors,
  ColourTarget,
  ColourGuardedAction,
  NonBrandResult,
  ShapeColorEntry,
  CleanUpResult,
  DetectNonBrandOptions,
} from "../../../types";
import { getActivePresentationBytes } from "./pptxBytes";
import { validateSelection } from "../../../utils/validateSelection";
import { DeferredWrites } from "../../../utils/deferredWrites";
import { assertNever } from "../../../utils/assertNever";

/**
 * Colour Tools operations belong to the Deckorate connector;
 */
const COLOR_CONNECTOR = "deckorate";

/** Report a colour-ops failure through the shared telemetry pipeline (PII-scrubbed). */
function logColourError(reason: string, type: string, e: unknown): void {
  logError(
    "error.connector.failure",
    { type, scrubbedMessage: scrubMessage(e instanceof Error ? e.message : String(e)) },
    { connectorSystemName: COLOR_CONNECTOR, reason },
  );
}

/** Low-severity counterpart to logColourError for the table-cell per-item
 * catches below — these are expected to occasionally skip one cell/side
 * without aborting the batch, so they log at "warn" (like mapPlotter.ts's
 * per-item catches) rather than "error", to stay out of error-rate alerting
 * while still leaving a trace instead of a silent, invisible skip. */
function logColourSkip(reason: string): void {
  logEvent("connector.action.invoke", "warn", { connectorSystemName: COLOR_CONNECTOR, reason });
}

// ── Internal type aliases ──────────────────────────────────────────────────────

type RawGroup = {
  shapes: { items: PowerPoint.Shape[]; load(s: string): void };
};

// `fill`, `font`, and `borders` all live directly on the real TableCell
// (PowerPointApi 1.9) — there is no `.body` wrapper, and `Border` has no
// `.visible` (only `.color`/`.transparency`/`.weight`), unlike ShapeLineFormat.
type RawCellBorder = { color: string; transparency: number; load(s: string): void };

// PowerPoint.TextRun (PowerPointApi 1.8) — a plain data record, not a live
// proxy like TextRange: `cell.textRuns` is read and written as a whole
// array, so word-level colour replacement means splitting the loaded runs
// in JS and assigning the rebuilt array back, rather than mutating each
// run's `.font.color` in place the way a shape's per-word TextRange slices
// (via getSubstring) already do.
type RawTextRun = { text: string; font?: PowerPoint.FontProperties };

type RawTableCell = {
  fill: PowerPoint.ShapeFill;
  font: PowerPoint.ShapeFont;
  // Plain string content (PowerPointApi 1.8) — used only for the "does this
  // cell have any text at all" check (hasAnyText); textRuns (1.9) below is
  // for actual word-level colour read/write, a strictly newer, narrower API
  // set that a cell's simple text content doesn't need to depend on.
  text: string;
  textRuns: RawTextRun[];
  borders: {
    top: RawCellBorder;
    bottom: RawCellBorder;
    left: RawCellBorder;
    right: RawCellBorder;
  };
  load(s: string): void;
};

type RawTableShape = {
  rowCount: number;
  columnCount: number;
  // The real Table class has no getCell() — only getCellOrNullObject().
  getCellOrNullObject(row: number, col: number): RawTableCell;
  load(s: string): void;
};

type SlideWithId = { id: string; load(s: string): void };

const CELL_BORDER_SIDES = ["top", "bottom", "left", "right"] as const;

// Queues `load` on every item, tolerating per-item failures, then syncs once
// (also tolerating sync failure) — the single-sync-per-batch shape shared by
// every scan/replace load step in this file.
async function loadAndSync<T>(
  context: PowerPoint.RequestContext,
  items: T[],
  load: (item: T) => void,
): Promise<void> {
  for (const item of items) {
    try { load(item); } catch { logColourSkip("load-and-sync-item"); }
  }
  try { await context.sync(); } catch { logColourSkip("load-and-sync-commit"); }
}

type ShapeWithNewFill = { shape: PowerPoint.Shape; newFillHex: string };

// ── Internal helpers ───────────────────────────────────────────────────────────

const FALLBACK_THEME: ThemeColors = {
  dk1: "1F2D3D",
  lt1: "FFFFFF",
  dk2: "213E6A",
  lt2: "E8EDF3",
  accent1: "0B5DAA",
  accent2: "E84855",
  accent3: "F5A623",
  accent4: "7AC943",
  accent5: "29ABE2",
  accent6: "8F54A9",
  hlink: "0563C1",
  folHlink: "954F72",
};

function hasPpt(): boolean {
  return (globalThis as { PowerPoint?: unknown }).PowerPoint !== undefined;
}

function ppt(): typeof PowerPoint {
  return (globalThis as { PowerPoint: typeof PowerPoint }).PowerPoint;
}

function isSolidFill(fill: PowerPoint.ShapeFill): boolean {
  try {
    return String(fill.type ?? "").toLowerCase() === "solid";
  } catch {
    logColourSkip("fill-type-read");
    return false;
  }
}


// shapeTypeOf / NON_TEXT_SHAPE_TYPES / supportsTextFrame now live in
// utils/shapeTypes.ts — see the import at the top of this file. They were local
// to this module until the action-chips selection reader needed the same
// "can this shape hold text?" rule; sharing one definition keeps the two from
// drifting apart.

// Splits a flattened shape list into regular shapes, table shapes, and
// unsupported shapes (COLOUR_BLOCKED_SHAPE_KINDS: pictures, charts) —
// shared by the scan/replace passes and the quick-action apply/clear
// functions, all of which need this exact same triage before doing
// anything else. Regular shapes support a mutable `.fill`/`.lineFormat`
// directly; tables route fill/line/font changes through their CELLS
// instead (see applyFillToTableCells & co.); unsupported shapes don't
// support a mutable fill/line at all and are surfaced to the caller so it
// can warn instead of letting the whole sync fail with a raw Office.js
// error code.
function splitShapesAndTables(
  allShapes: PowerPoint.Shape[],
): { regularShapes: PowerPoint.Shape[]; tableShapes: PowerPoint.Shape[]; unsupportedShapes: PowerPoint.Shape[] } {
  const regularShapes: PowerPoint.Shape[] = [];
  const tableShapes: PowerPoint.Shape[] = [];
  const unsupportedShapes: PowerPoint.Shape[] = [];
  for (const shape of allShapes) {
    const t = shapeTypeOf(shape);
    // Same COLOUR_BLOCKED_SHAPE_KINDS as the guarded-action pre-flight check
    // (requireSupportedShapes) — Replace doesn't call that guard (it's
    // designed to keep going across whatever else is in scope rather than
    // abort the whole multi-slide operation), so it needs its own exclusion
    // here instead of silently attempting (and failing) a fill/font/line
    // write against a shape type that can't take one.
    if (COLOUR_BLOCKED_SHAPE_KINDS.has(t)) { unsupportedShapes.push(shape); continue; }
    if (t === "table") tableShapes.push(shape);
    else regularShapes.push(shape);
  }
  return { regularShapes, tableShapes, unsupportedShapes };
}

// PowerPoint.TextRange has no `.paragraphs` collection (unlike Word.TextRange or
// OneNote — this API surface simply doesn't exist in the PowerPoint JS API). Whole-range
// reads of font.color return null as soon as a shape has more than one colour, and
// there is no per-run object to read/write instead. Paragraph boundaries are
// recovered from the plain text (paragraph breaks are "\r"), and each paragraph is
// further split on whitespace into per-WORD ranges — a single paragraph with two
// differently-coloured words (e.g. "Hello World", no paragraph break between them)
// would otherwise be sliced as one range whose font.color reads back null (mixed),
// so neither word's colour is ever detected or replaced. Each slice is cut out with
// getSubstring(), which DOES support independent font.color read/write per slice.
function splitIntoParagraphRanges(
  textRange: PowerPoint.TextRange,
  text: string,
): PowerPoint.TextRange[] {
  if (!text) return [];
  const ranges: PowerPoint.TextRange[] = [];
  let offset = 0;
  for (const line of text.split("\r")) {
    for (const match of line.matchAll(/\S+/g)) {
      ranges.push(textRange.getSubstring(offset + match.index, match[0].length));
    }
    offset += line.length + 1;
  }
  return ranges;
}

// Slices a text range into per-paragraph ranges and queues each slice's
// font.color load — shared by every scan/replace pass that needs per-
// paragraph colour (a shape or a table cell's text body).
function buildParagraphFontRanges(textRange: PowerPoint.TextRange): PowerPoint.TextRange[] {
  const ranges = splitIntoParagraphRanges(textRange, textRange.text);
  for (const r of ranges) {
    try { r.font.load("color"); } catch { logColourSkip("paragraph-range-font-load"); }
  }
  return ranges;
}

// Builds per-paragraph font ranges for a batch of already-text-loaded shapes
// and syncs once — shared by getSelectedShapeColours, scanFontNonBrand, and
// applyReplaceToShapeList's font pass.
async function loadParagraphFontRangesForShapes(
  context: PowerPoint.RequestContext,
  textCapableShapes: PowerPoint.Shape[],
): Promise<Map<PowerPoint.Shape, PowerPoint.TextRange[]>> {
  const fontRangesByShape = new Map<PowerPoint.Shape, PowerPoint.TextRange[]>();
  for (const shape of textCapableShapes) {
    try {
      fontRangesByShape.set(shape, buildParagraphFontRanges(shape.textFrame.textRange));
    } catch { logColourSkip("paragraph-ranges-build"); }
  }
  try { await context.sync(); } catch { logColourSkip("paragraph-ranges-sync"); }
  return fontRangesByShape;
}

// Loads id/name/type on a batch of top-level shapes and flattens groups
// (up to 6 levels) — shared by the per-slide loops in detectNonBrandColours
// and replaceColours. Returns null when the sync itself fails, so the
// caller can `continue` past that slide exactly as it did inline before.
async function loadAndFlattenShapes(
  context: PowerPoint.RequestContext,
  shapeItems: PowerPoint.Shape[],
): Promise<PowerPoint.Shape[] | null> {
  for (const shape of shapeItems) {
    try {
      (shape as unknown as { load(s: string): void }).load("id,name,type");
    } catch {
      logColourSkip("slide-shape-id-load");
    }
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("slide-shapes-load-sync");
    return null;
  }
  return flattenGroupShapes(context, shapeItems, 0, 6);
}

// Collects every cell of one table via getCellOrNullObject(r,c) — assumes
// rowCount/columnCount are already loaded on this exact `tbl` reference.
function collectCellsFromTable(tbl: RawTableShape): RawTableCell[] {
  const cells: RawTableCell[] = [];
  try {
    for (let r = 0; r < tbl.rowCount; r++) {
      for (let c = 0; c < tbl.columnCount; c++) {
        try { cells.push(tbl.getCellOrNullObject(r, c)); } catch { logColourSkip("table-cell-get"); }
      }
    }
  } catch { logColourSkip("table-row-column-count"); }
  return cells;
}

// Loads rowCount/columnCount on every table shape and collects every cell
// via getCellOrNullObject(r,c) — shared by scanShapesForNonBrand and
// applyReplaceToShapeList, both of which need flat cell access regardless
// of which fill/line/font target is active.
//
// `Shape.getTable()` is a method, not a cached property — each call returns
// a fresh, unloaded proxy. Calling it once per shape here and reusing that
// same reference for both `.load()` and the later row/column read is
// required; calling it again after the sync would read from a brand-new,
// never-loaded object and silently see rowCount/columnCount as unset.
async function collectTableCells(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
): Promise<RawTableCell[]> {
  if (tableShapes.length === 0) return [];

  const tables: RawTableShape[] = [];
  for (const shape of tableShapes) {
    try {
      const tbl = (shape as unknown as { getTable(): RawTableShape }).getTable();
      tbl.load("rowCount,columnCount");
      tables.push(tbl);
    } catch { logColourSkip("table-get-table"); }
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("table-cells-sync");
    return [];
  }

  return tables.flatMap(collectCellsFromTable);
}

// ── Shape selection helper ────────────────────────────────────────────────────

async function getSelectedShapesFlat(
  context: PowerPoint.RequestContext,
): Promise<PowerPoint.Shape[]> {
  let shapesToProcess: PowerPoint.Shape[] = [];

  // Try to get explicitly selected shapes (API set 1.5+)
  try {
    type Selection = { load(s: string): void; items: PowerPoint.Shape[] };
    const sel = (context.presentation as unknown as { getSelectedShapes(): Selection }).getSelectedShapes();
    sel.load("items");
    await context.sync();
    if (sel.items.length > 0) shapesToProcess = sel.items;
  } catch {
    // Expected on older Office.js builds without getSelectedShapes(), or
    // when nothing is selected — falls through to the active-slide scan
    // below, so this is a skip, not a failure.
    logColourSkip("selected-shapes-api-unavailable");
  }

  // Fall back to all shapes on the selected/active slide
  if (shapesToProcess.length === 0) {
    try {
      const slides = context.presentation.getSelectedSlides();
      slides.load("items");
      await context.sync();
      if (slides.items.length === 0) return [];
      const slide = slides.items[0]!;
      slide.shapes.load("items");
      await context.sync();
      shapesToProcess = slide.shapes.items;
    } catch {
      logColourSkip("selected-shapes-slide-fallback-failed");
      return [];
    }
  }

  if (shapesToProcess.length === 0) return [];

  // Load id,name,type so flattenGroupShapes can classify groups vs. leaf shapes.
  for (const shape of shapesToProcess) {
    try { (shape as unknown as { load(s: string): void }).load("id,name,type"); } catch { logColourSkip("selected-shape-id-load"); }
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("selected-shapes-flatten-sync");
    return shapesToProcess;
  }

  return flattenGroupShapes(context, shapesToProcess, 0, 6);
}

// ── Shape-selection guard ─────────────────────────────────────────────────────
// No Fill / No Line / apply Fill·Font·Line / On-Brand Fill / Shuffle must not
// silently fall back to "every shape on the active slide" the way
// getSelectedShapesFlat() does above — with nothing explicitly selected they
// block and ask the user to select a shape instead of repainting the whole
// slide. Replace Colors and Non-Brand detection are deliberately NOT guarded:
// both are designed to run slide/presentation-wide when nothing is selected.

/** Thrown by requireSelectedShapes() — kept as a distinct type so telemetry
 * can tell "user hasn't selected a shape" apart from a genuine Office.js
 * failure instead of conflating the two under one error type. */
class ColourSelectionRequiredError extends Error {
  constructor(action: ColourGuardedAction) {
    super(`Select minimum one shape to ${action}`);
    this.name = "ColourSelectionRequiredError";
  }
}

// Shared with useColourTools.ts's requireSelectionForReplace (the
// open-the-modal-time guard) so both guards show byte-identical copy.
export const REPLACE_SELECTION_REQUIRED_MESSAGE = "Please select a shape to replace its color.";

/** Thrown by replaceColours() when its "Selected Shapes" scope has nothing
 * selected. Kept separate from ColourSelectionRequiredError (rather than
 * adding "Replace Colors" to ColourGuardedAction) because Replace Colors'
 * Current Slide/Range/All Slides scopes still resolve their own target
 * slides with no selection dependency once a replace is underway — only
 * this scope, and the panel-level open-the-modal guard in
 * useColourTools.ts, specifically require a selection up front. */
class ColourReplaceSelectionRequiredError extends Error {
  constructor() {
    super(REPLACE_SELECTION_REQUIRED_MESSAGE);
    this.name = "ColourReplaceSelectionRequiredError";
  }
}

/** Thrown when a caller passes a hex string normalizeHex() can't parse —
 * kept distinct so telemetry doesn't lump "user typed garbage" in with a
 * genuine Office.js failure, and so the UI can show a targeted message
 * instead of silently applying whatever text was typed. */
class ColourInvalidHexError extends Error {
  constructor(hex: string) {
    super(`"${hex}" isn't a valid colour. Use a 6-digit hex code like #0B5DAA.`);
    this.name = "ColourInvalidHexError";
  }
}

/** Validates a user-supplied hex string, returning it as a normalized
 * `#RRGGBB` string. Every apply-colour entry point must call this instead
 * of falling back to the raw input — Office.js's setSolidColor() doesn't
 * reject a malformed hex string synchronously, so an unvalidated fallback
 * silently "applies" garbage input with no error at all. */
function requireValidHex(hex: string): string {
  const normalized = normalizeHex(hex);
  if (!normalized) throw new ColourInvalidHexError(hex);
  return `#${normalized}`;
}

/** Call before opening the action's own `ppt().run()` — a single shared
 * pre-flight check instead of each feature probing `getSelectedShapes()`
 * on its own. */
async function requireSelectedShapes(action: ColourGuardedAction): Promise<void> {
  if (!(await validateSelection())) throw new ColourSelectionRequiredError(action);
}

// ── Unsupported-shape guard ────────────────────────────────────────────────────
// Picture and chart shapes don't support a mutable fill/line the way regular
// shapes do (touching either throws InvalidArgument at context.sync() time,
// rejecting the whole batch, not just that shape); Shuffle and On-Brand Fill
// also skip table shapes rather than attempting a theme-snap against a
// table's cells. In every case the action still applies to whatever else was
// selected — this only reports the shapes it had to leave untouched, in
// place of a raw Office.js error code.

/** Thrown when a table shape is selected for an action that can't yet snap
 * its per-cell colours to a theme (On-Brand Fill / Shuffle — see the
 * comment on those functions) — kept distinct so telemetry doesn't lump
 * "expected, shape-type limitation" in with a genuine failure. Message
 * names the exact action attempted, not a generic/static string. */
class ColourUnsupportedShapeError extends Error {
  constructor(action: ColourGuardedAction) {
    super(`This shape doesn't support "${action}".`);
    this.name = "ColourUnsupportedShapeError";
  }
}

// Shape types Colour Tools can't touch at all, keyed by Office.js `.type`.
// Picture: its fill IS the image render, nothing meaningful to solid-fill.
// Chart: not a technical limitation (a chart's own plot-background fill is
// in fact a real, settable solid fill — see the "recolours a chart's own
// outer fill" history in this file's tests) but blocked by product decision
// regardless, same as Picture.
const COLOUR_BLOCKED_SHAPE_KINDS = new Set(["image", "picture", "chart"]);

/** Thrown when the current selection contains a shape type from
 * COLOUR_BLOCKED_SHAPE_KINDS. Skip-the-bad-shape-but-still-apply-to-the-rest
 * was confusing in practice, so any such shape ANYWHERE in the selection
 * blocks the whole action instead of a raw Office.js "InvalidArgument"
 * error partway through. Same "name the exact action" message as
 * ColourUnsupportedShapeError. */
class ColourUnsupportedSelectionError extends Error {
  constructor(action: ColourGuardedAction) {
    super(`This shape doesn't support "${action}".`);
    this.name = "ColourUnsupportedSelectionError";
  }
}

/** Whether any currently-selected shape (including inside groups) is a type
 * Colour Tools can't touch at all — see COLOUR_BLOCKED_SHAPE_KINDS. Runs its
 * own ppt().run(), the same way validateSelection() does. */
async function hasBlockedSelection(): Promise<boolean> {
  if (!hasPpt()) return false;
  try {
    return await ppt().run(async (context) => {
      const shapes = await getSelectedShapesFlat(context);
      return shapes.some((shape) => COLOUR_BLOCKED_SHAPE_KINDS.has(shapeTypeOf(shape)));
    });
  } catch (e) {
    logColourError("colour-blocked-selection-check", "ColourBlockedSelectionCheckError", e);
    return false;
  }
}

/** Call before opening the action's own `ppt().run()` — blocks the whole
 * action up front if a picture/chart/etc. is anywhere in the current
 * selection. */
async function requireSupportedShapes(action: ColourGuardedAction): Promise<void> {
  if (await hasBlockedSelection()) throw new ColourUnsupportedSelectionError(action);
}

/** Same check as requireSupportedShapes, for the two callers that can't use
 * ColourGuardedAction (Replace Colors / Non-Brand Colors — see
 * REPLACE_SELECTION_REQUIRED_MESSAGE's comment on why those two are kept
 * out of that union) and that need the message as a plain string to set
 * into their own error state, rather than an exception to catch. Returns
 * null when the selection is fine. Used for the "Selected Shapes" scope
 * specifically — opening Replace on a chart/picture, or scanning a
 * chart/picture for Non-Brand colours, used to silently show nothing
 * instead of explaining why. */
export async function getUnsupportedSelectionMessage(action: string): Promise<string | null> {
  return (await hasBlockedSelection()) ? `This shape doesn't support "${action}".` : null;
}

/** Thrown by applyFontColour when every candidate (shape/cell) in the
 * selection has no text at all — setting `.font.color` on an empty text
 * range is a harmless property write Office.js doesn't reject, so without
 * this the action silently reported "Font colour applied." even though
 * nothing was visibly different. */
class ColourNoTextError extends Error {
  constructor() {
    super("No text to apply a colour to.");
    this.name = "ColourNoTextError";
  }
}

/** Picks the telemetry `type` tag for a caught colour-ops error: a
 * validation guard's own name when it fired, otherwise the caller's
 * action-specific fallback. */
function colourErrorType(e: unknown, fallback: string): string {
  if (e instanceof ColourSelectionRequiredError) return e.name;
  if (e instanceof ColourReplaceSelectionRequiredError) return e.name;
  if (e instanceof ColourInvalidHexError) return e.name;
  if (e instanceof ColourUnsupportedShapeError) return e.name;
  if (e instanceof ColourUnsupportedSelectionError) return e.name;
  if (e instanceof ColourNoTextError) return e.name;
  return fallback;
}

// ── Extract theme colours ──────────────────────────────────────────────────────
// Reads the PPTX binary via getFileAsync, unzips it with fflate, walks the
// OOXML rels chain (slide → slideLayout → slideMaster → theme) to find the
// correct themeN.xml, and DOM-parses the <a:clrScheme>. This mirrors the
// reference (POCaddin_V2) exactly and works regardless of Office.js API version.

const NS_A = "http://schemas.openxmlformats.org/drawingml/2006/main";

// Reads a rels file and returns the first Target matching targetRe, mapped via abs().
function resolveRel(
  zip: Unzipped,
  relsPath: string,
  targetRe: RegExp,
  abs: (t: string) => string,
): string | null {
  const file = zip[relsPath];
  if (!file) return null;
  const xml = strFromU8(file);
  const tags = xml.match(/<Relationship\b[^>]*\/>/g) ?? [];
  for (const tag of tags) {
    const m = /\bTarget="([^"]+)"/.exec(tag);
    if (m && targetRe.test(m[1]!)) return abs(m[1]!);
  }
  return null;
}

// Walks the rels chain for a single slide → layout → master → theme.
function resolveSlideThemePath(zip: Unzipped, slideFile: string): string | null {
  const layoutAbs = resolveRel(
    zip,
    `ppt/slides/_rels/${slideFile}.rels`,
    /slideLayout\d+\.xml/,
    (t) => `ppt/slideLayouts/${t.split("/").pop()}`,
  );
  if (!layoutAbs) return null;
  const masterAbs = resolveRel(
    zip,
    `ppt/slideLayouts/_rels/${layoutAbs.split("/").pop()}.rels`,
    /slideMaster\d+\.xml/,
    (t) => `ppt/slideMasters/${t.split("/").pop()}`,
  );
  if (!masterAbs) return null;
  return resolveRel(
    zip,
    `ppt/slideMasters/_rels/${masterAbs.split("/").pop()}.rels`,
    /theme\d+\.xml/,
    (t) => `ppt/theme/${t.split("/").pop()}`,
  );
}

// Returns the theme path by walking the rels chain from slide1.
// Matches DeCKorate's _resolveActiveSlideChain which also uses slidePaths[0].
function resolveActiveThemePath(zip: Unzipped): string | null {
  const slidePaths = Object.keys(zip)
    .filter((p) => /^ppt\/slides\/slide\d+\.xml$/.test(p))
    .sort((a, b) => {
      const na = Number.parseInt(/slide(\d+)/.exec(a)![1]!, 10);
      const nb = Number.parseInt(/slide(\d+)/.exec(b)![1]!, 10);
      return na - nb;
    });
  if (slidePaths.length === 0) return null;
  return resolveSlideThemePath(zip, slidePaths[0]!.split("/").pop()!);
}

function parseThemeXml(xml: string): ThemeColors | null {
  const doc = new DOMParser().parseFromString(xml, "text/xml");
  const clrScheme = doc.getElementsByTagNameNS(NS_A, "clrScheme")[0];
  if (!clrScheme) return null;

  const slots = ["dk1", "dk2", "lt1", "lt2", "accent1", "accent2", "accent3", "accent4", "accent5", "accent6", "hlink", "folHlink"] as const;
  const colors: Partial<ThemeColors> = {};

  for (const name of slots) {
    const el = clrScheme.getElementsByTagNameNS(NS_A, name)[0];
    if (!el) continue;
    const srgb = el.getElementsByTagNameNS(NS_A, "srgbClr")[0];
    if (srgb) { colors[name] = srgb.getAttribute("val") ?? undefined; continue; }
    const sys = el.getElementsByTagNameNS(NS_A, "sysClr")[0];
    if (sys) { colors[name] = (sys.getAttribute("lastClr") ?? sys.getAttribute("val")) ?? undefined; }
  }

  if (Object.keys(colors).length === 0) return null;
  const fb = FALLBACK_THEME;
  return {
    dk1: colors.dk1 ?? fb.dk1,
    lt1: colors.lt1 ?? fb.lt1,
    dk2: colors.dk2 ?? fb.dk2,
    lt2: colors.lt2 ?? fb.lt2,
    accent1: colors.accent1 ?? fb.accent1,
    accent2: colors.accent2 ?? fb.accent2,
    accent3: colors.accent3 ?? fb.accent3,
    accent4: colors.accent4 ?? fb.accent4,
    accent5: colors.accent5 ?? fb.accent5,
    accent6: colors.accent6 ?? fb.accent6,
    hlink: colors.hlink ?? fb.hlink,
    folHlink: colors.folHlink ?? fb.folHlink,
  };
}

export async function extractThemeColors(): Promise<ThemeColors> {
  try {
    const buf = await getActivePresentationBytes();
    const zip = unzipSync(new Uint8Array(buf));

    // Walk the rels chain; fall back to theme1.xml
    const themePath = resolveActiveThemePath(zip) ?? "ppt/theme/theme1.xml";
    const themeFile = zip[themePath];
    if (!themeFile) return FALLBACK_THEME;

    const xml = strFromU8(themeFile);
    return parseThemeXml(xml) ?? FALLBACK_THEME;
  } catch (e) {
    logColourError("colour-theme", "ColourThemeError", e);
    return FALLBACK_THEME;
  }
}

// ── Table-cell colour helpers ──────────────────────────────────────────────────
// PowerPoint table shapes don't support a shape-level `.fill`/`.lineFormat`
// the way regular shapes do — a table's colour lives per CELL. Touching
// `.fill`/`.lineFormat` directly on a table shape throws InvalidArgument at
// context.sync() time, rejecting the WHOLE batch. So quick actions route a
// table through its cells instead of skipping it outright — "apply fill
// colour to a table" means "apply it to every cell's body fill."
//
// Each of these syncs its own queued cell writes SEPARATELY from the
// caller's own final sync (for whatever regular shapes are also in the
// selection), and returns whether that sync succeeded. Two reasons: (1) a
// cell property the host rejects (e.g. .clear() on a freshly-inserted
// table's cell — a fill with no explicit override yet) would otherwise take
// the ENTIRE shared batch down, undoing every regular shape's already-queued,
// perfectly valid write too; (2) a genuine failure here can now surface as
// the SAME friendly "This shape doesn't support ..." message On-Brand Fill/
// Shuffle already use for tables, instead of an uncaught, raw Office.js
// error reaching the user.

async function applyFillToTableCells(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
  color: string,
): Promise<boolean> {
  const cells = await collectTableCells(context, tableShapes);
  if (cells.length === 0) return true;
  for (const cell of cells) {
    try { cell.fill.setSolidColor(color); } catch { logColourSkip("table-cell-fill-apply"); }
  }
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("table-cell-fill-apply-sync");
    return false;
  }
}

async function clearFillOnTableCells(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
): Promise<boolean> {
  const cells = await collectTableCells(context, tableShapes);
  if (cells.length === 0) return true;
  for (const cell of cells) {
    try { cell.fill.clear(); } catch { logColourSkip("table-cell-fill-clear"); }
  }
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("table-cell-fill-clear-sync");
    return false;
  }
}

async function applyLineToTableCells(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
  color: string,
): Promise<boolean> {
  const cells = await collectTableCells(context, tableShapes);
  if (cells.length === 0) return true;
  for (const cell of cells) {
    for (const side of CELL_BORDER_SIDES) {
      try { cell.borders[side].color = color; } catch { logColourSkip("table-cell-border-apply"); }
    }
  }
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("table-cell-border-apply-sync");
    return false;
  }
}

// Border has no `.visible` toggle (unlike ShapeLineFormat) — fully
// transparent is the closest real equivalent to "no line" for a cell border.
async function clearLineOnTableCells(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
): Promise<boolean> {
  const cells = await collectTableCells(context, tableShapes);
  if (cells.length === 0) return true;
  for (const cell of cells) {
    for (const side of CELL_BORDER_SIDES) {
      try { cell.borders[side].transparency = 1; } catch { logColourSkip("table-cell-border-clear"); }
    }
  }
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("table-cell-border-clear-sync");
    return false;
  }
}

async function applyFontToTableCells(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
  color: string,
): Promise<boolean> {
  if (cells.length === 0) return true;
  for (const cell of cells) {
    try { cell.font.color = color; } catch { logColourSkip("table-cell-font-apply"); }
  }
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("table-cell-font-apply-sync");
    return false;
  }
}

// ── Apply fill ─────────────────────────────────────────────────────────────────

export async function applyFillColour(hex: string): Promise<void> {
  if (!hasPpt()) return;
  try {
    const color = requireValidHex(hex);
    await requireSelectedShapes("Apply Fill Color");
    await requireSupportedShapes("Apply Fill Color");
    let cellsFailed = false;
    await ppt().run(async (context) => {
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      for (const shape of regularShapes) {
        try { shape.fill.setSolidColor(color); } catch { logColourSkip("apply-fill-shape"); }
      }
      cellsFailed = !(await applyFillToTableCells(context, tableShapes, color));
      await context.sync();
    });
    if (cellsFailed) throw new ColourUnsupportedShapeError("Apply Fill Color");
  } catch (e) {
    logColourError("colour-fill", colourErrorType(e, "ColourFillError"), e);
    throw e;
  }
}

// ── Apply font colour ──────────────────────────────────────────────────────────

// getSelectedTextRangeOrNullObject reports a non-null range with the
// SHAPE'S ENTIRE TEXT the moment a text frame merely has focus — e.g. the
// shape was just object-selected, or the cursor was placed/tabbed into it —
// not only when the user has genuinely drag-highlighted a shorter
// substring. Treating that as "a word is highlighted" would silently
// narrow whole-shape font actions (colour, detect, replace) down to a
// single colour scope the user never asked for, which is exactly what
// broke multi-colour detection for Replace's "Selected Shapes" scope: a
// shape was selected (no word highlighted), yet detection only offered one
// colour instead of every distinct word colour in it. A genuine highlight
// is always a proper (strictly shorter) subset of the full text; anything
// else — including the full text reported back as "selected" — falls
// through to the existing whole-shape/whole-cell scan instead.
async function isGenuinePartialTextSelection(
  context: PowerPoint.RequestContext,
  textRange: PowerPoint.TextRange,
): Promise<boolean> {
  const fullRange = textRange.getParentTextFrame().textRange;
  fullRange.load("text");
  await context.sync();
  return textRange.text.length < fullRange.text.length;
}

// Whether the current selection includes a table — a table cell's text has
// no equivalent to a shape's live text-range selection (getParentTextFrame
// resolves to the shape's own text frame concept, which tables don't have),
// so getSelectedTextRangeOrNullObject()'s behaviour when a table/cell is
// selected is unreliable rather than a genuine "this word is highlighted"
// signal. Every "live text range" shortcut below skips itself entirely
// when this is true, falling through to the normal whole-shape/whole-cell
// path instead — this was silently narrowing (or entirely skipping) font
// detection/replace/apply for a selected table otherwise. Runs its own
// getSelectedShapesFlat rather than accepting a pre-fetched list, since
// these shortcuts are deliberately attempted even when nothing is
// "shape-selected" at all (e.g. actively editing text inside a shape) —
// this check must not require a non-empty selection to be meaningful.
async function selectionIncludesTable(context: PowerPoint.RequestContext): Promise<boolean> {
  try {
    const shapes = await getSelectedShapesFlat(context);
    return shapes.some((s) => shapeTypeOf(s) === "table");
  } catch {
    logColourSkip("selection-includes-table-check");
    return false;
  }
}

// Colours just the user's live text selection (e.g. one highlighted word
// inside a text box or table cell) instead of the whole shape — the one
// piece of per-word granularity Office.js actually exposes directly:
// getSelectedShapes() only ever reports WHICH shape is selected, never
// which characters within it, so this is the only way "select one word,
// apply a colour" can affect just that word rather than the whole text
// frame. Returns false (not an error) whenever there's nothing to act on
// here — no text highlighted, just a shape/cursor position — so the caller
// falls through to the existing whole-shape/whole-cell behaviour unchanged.
async function applyFontColourToSelectedTextRange(color: string): Promise<boolean> {
  try {
    return await ppt().run(async (context) => {
      if (await selectionIncludesTable(context)) return false;
      const textRange = context.presentation.getSelectedTextRangeOrNullObject();
      textRange.load("isNullObject,text");
      await context.sync();
      if (textRange.isNullObject || !textRange.text) return false;
      if (!(await isGenuinePartialTextSelection(context, textRange))) return false;
      textRange.font.color = color;
      await context.sync();
      return true;
    });
  } catch {
    // Covers hosts predating PowerPointApi 1.5 as well as a genuine
    // failure — either way, this is an optional enhancement over the
    // whole-shape path below, not a reason to abort the whole action.
    logColourSkip("apply-font-selected-text-range");
    return false;
  }
}

// Whether at least one candidate (shape or table cell) actually has text —
// checked before writing font colour, since setting `.font.color` on an
// empty text range/cell is a harmless no-op property write Office.js
// doesn't reject. Fails OPEN (assumes text exists) if the check's own load
// or sync fails, so a transient failure here degrades to the previous
// behaviour rather than blocking a legitimate apply.
//
// Cells load the plain `text` property (PowerPointApi 1.8), not `textRuns`
// (1.9) — a cell with visible text but no run-level formatting ever
// explicitly touched can read back an empty `textRuns` array even though
// it plainly has text, which was wrongly blocking "Apply Font Color" on an
// otherwise perfectly normal table cell with a "No text to apply a colour
// to." error. `text` is the simpler, more broadly-supported signal for
// this yes/no question — word-level colour read/write still uses
// `textRuns` elsewhere (see replaceCellFont), which needs the per-run
// detail this check doesn't.
async function hasAnyText(
  context: PowerPoint.RequestContext,
  textCapableShapes: PowerPoint.Shape[],
  cells: RawTableCell[],
): Promise<boolean> {
  if (textCapableShapes.length === 0 && cells.length === 0) return true;
  for (const shape of textCapableShapes) {
    try { shape.textFrame.textRange.load("text"); } catch { logColourSkip("font-text-check-load"); }
  }
  for (const cell of cells) {
    try { cell.load("text"); } catch { logColourSkip("font-text-check-load"); }
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("font-text-check-sync");
    return true;
  }
  const shapeHasText = textCapableShapes.some((s) => {
    try { return s.textFrame.textRange.text.length > 0; } catch { return false; }
  });
  const cellHasText = cells.some((c) => {
    try { return c.text.length > 0; } catch { return false; }
  });
  return shapeHasText || cellHasText;
}

export async function applyFontColour(hex: string): Promise<void> {
  if (!hasPpt()) return;
  try {
    const color = requireValidHex(hex);

    if (await applyFontColourToSelectedTextRange(color)) return;

    await requireSelectedShapes("Apply Font Color");
    await requireSupportedShapes("Apply Font Color");
    let cellsFailed = false;
    await ppt().run(async (context) => {
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      const textCapableShapes = regularShapes.filter(supportsTextFrame);
      const cells = await collectTableCells(context, tableShapes);

      if (!(await hasAnyText(context, textCapableShapes, cells))) {
        throw new ColourNoTextError();
      }

      // Writing to the whole range recolours it uniformly even when the prior
      // per-paragraph colours were mixed — no paragraph split needed for a write.
      for (const shape of textCapableShapes) {
        try { shape.textFrame.textRange.font.color = color; } catch { logColourSkip("apply-font-shape"); }
      }
      cellsFailed = !(await applyFontToTableCells(context, cells, color));
      await context.sync();
    });
    if (cellsFailed) throw new ColourUnsupportedShapeError("Apply Font Color");
  } catch (e) {
    logColourError("colour-font", colourErrorType(e, "ColourFontError"), e);
    throw e;
  }
}

// ── Apply line/border colour ───────────────────────────────────────────────────

export async function applyLineColour(hex: string): Promise<void> {
  if (!hasPpt()) return;
  try {
    const color = requireValidHex(hex);
    await requireSelectedShapes("Apply Line Color");
    await requireSupportedShapes("Apply Line Color");
    let cellsFailed = false;
    await ppt().run(async (context) => {
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      for (const shape of regularShapes) {
        try { shape.lineFormat.color = color; } catch { logColourSkip("apply-line-shape"); }
      }
      cellsFailed = !(await applyLineToTableCells(context, tableShapes, color));
      await context.sync();
    });
    if (cellsFailed) throw new ColourUnsupportedShapeError("Apply Line Color");
  } catch (e) {
    logColourError("colour-line", colourErrorType(e, "ColourLineError"), e);
    throw e;
  }
}

// ── No fill ────────────────────────────────────────────────────────────────────

export async function applyNoFill(): Promise<void> {
  if (!hasPpt()) return;
  try {
    await requireSelectedShapes("No Fill");
    await requireSupportedShapes("No Fill");
    let cellsFailed = false;
    await ppt().run(async (context) => {
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      for (const shape of regularShapes) {
        try { shape.fill.clear(); } catch { logColourSkip("clear-fill-shape"); }
      }
      cellsFailed = !(await clearFillOnTableCells(context, tableShapes));
      await context.sync();
    });
    if (cellsFailed) throw new ColourUnsupportedShapeError("No Fill");
  } catch (e) {
    logColourError("colour-no-fill", colourErrorType(e, "ColourNoFillError"), e);
    throw e;
  }
}

// ── No line ────────────────────────────────────────────────────────────────────

export async function applyNoLine(): Promise<void> {
  if (!hasPpt()) return;
  try {
    await requireSelectedShapes("No Line");
    await requireSupportedShapes("No Line");
    let cellsFailed = false;
    await ppt().run(async (context) => {
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      for (const shape of regularShapes) {
        try { shape.lineFormat.visible = false; } catch { logColourSkip("clear-line-shape"); }
      }
      cellsFailed = !(await clearLineOnTableCells(context, tableShapes));
      await context.sync();
    });
    if (cellsFailed) throw new ColourUnsupportedShapeError("No Line");
  } catch (e) {
    logColourError("colour-no-line", colourErrorType(e, "ColourNoLineError"), e);
    throw e;
  }
}

// ── On-brand fill / shuffle shared helper ───────────────────────────────────────
// Adapts text colour on every shape that got a new fill — shared by
// applyOnBrandFill and shuffleColour, whose final phase is identical.

async function adaptTextColorForNewFills(
  context: PowerPoint.RequestContext,
  updatedShapes: ShapeWithNewFill[],
  lightTextColor: string,
  darkTextColor: string,
  deferred: DeferredWrites,
): Promise<void> {
  const textUpdatedShapes = updatedShapes.filter(({ shape }) => supportsTextFrame(shape));
  if (textUpdatedShapes.length === 0) return;

  for (const { shape } of textUpdatedShapes) {
    try { shape.textFrame.textRange.font.load("color"); } catch { logColourSkip("adapt-text-color-load"); }
  }
  try { await context.sync(); } catch { logColourSkip("adapt-text-color-sync"); }

  for (const { shape, newFillHex } of textUpdatedShapes) {
    const fontColor = pickContrastText(newFillHex, lightTextColor, darkTextColor);
    deferred.queue("text-color-adapt", () => { shape.textFrame.textRange.font.color = `#${fontColor}`; });
  }
}

// ── On-brand fill ──────────────────────────────────────────────────────────────
// Matches reference applyOnBrandFill, extended to also snap the line/border:
//   1. Skip non-solid and white/near-white fills (backgrounds)
//   2. Snap fill to nearest non-white theme color via findNearestThemeFillColor
//   3. Snap line/border color to nearest non-white theme color the same way
//   4. Adapt text color based on the new fill's perceived luminance

async function loadFillAndLineForSnap(
  context: PowerPoint.RequestContext,
  shapes: PowerPoint.Shape[],
): Promise<void> {
  for (const shape of shapes) {
    try { shape.fill.load("type,foregroundColor"); } catch { logColourSkip("on-brand-fill-load"); }
    try { shape.lineFormat.load("color,visible"); } catch { logColourSkip("on-brand-line-load"); }
  }
  await context.sync();
}

// Snaps one shape's fill to the nearest non-white theme colour. Returns the
// new fill hex when the shape should have its text colour adapted (tracked
// even when the fill was already on-brand), or null when it was skipped
// (non-solid, unreadable, or white/near-white background).
function snapShapeFillToTheme(shape: PowerPoint.Shape, theme: ThemeColors, deferred: DeferredWrites): string | null {
  try {
    if (!isSolidFill(shape.fill)) return null;
    const currentHex = normalizeHex(shape.fill.foregroundColor);
    if (!currentHex || isNearWhite(currentHex)) return null;

    // findNearestThemeFillColor excludes near-white theme entries (>220 per
    // channel) so a light-grey shape never snaps to white and disappears.
    const nearest = findNearestThemeFillColor(currentHex, theme);
    if (!nearest) return null;
    if (nearest.hex !== currentHex) deferred.queue("on-brand-fill-snap", () => shape.fill.setSolidColor(`#${nearest.hex}`));
    return nearest.hex;
  } catch {
    logColourSkip("on-brand-fill-snap-read");
    return null;
  }
}

function snapShapeLineToTheme(shape: PowerPoint.Shape, theme: ThemeColors, deferred: DeferredWrites): void {
  try {
    if (shape.lineFormat.visible === false) return;
    const currentLineHex = normalizeHex(shape.lineFormat.color);
    if (!currentLineHex) return;
    const nearestLine = findNearestThemeFillColor(currentLineHex, theme);
    if (nearestLine && nearestLine.hex !== currentLineHex) {
      deferred.queue("on-brand-line-snap", () => { shape.lineFormat.color = `#${nearestLine.hex}`; });
    }
  } catch { logColourSkip("on-brand-line-read"); }
}

// ── On-brand fill: table cells ──────────────────────────────────────────────
// Same three-step snap as the shape-level helpers above (skip non-solid/
// near-white, snap to nearest non-white theme colour, adapt font contrast),
// just addressed per cell instead of per shape — a table's colour lives per
// cell, the same reason applyFillColour/applyLineColour/applyFontColour all
// route through cells instead of touching the table shape directly.

type CellWithNewFill = { cell: RawTableCell; newFillHex: string };

async function loadCellFillAndLineForSnap(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
): Promise<void> {
  if (cells.length === 0) return;
  for (const cell of cells) {
    try { cell.fill.load("type,foregroundColor"); } catch { logColourSkip("on-brand-cell-fill-load"); }
    loadCellBorderColors(cell);
  }
  try { await context.sync(); } catch { logColourSkip("on-brand-cell-load-sync"); }
}

function snapCellFillToTheme(cell: RawTableCell, theme: ThemeColors, deferred: DeferredWrites): string | null {
  try {
    if (!isSolidFill(cell.fill)) return null;
    const currentHex = normalizeHex(cell.fill.foregroundColor);
    if (!currentHex || isNearWhite(currentHex)) return null;
    const nearest = findNearestThemeFillColor(currentHex, theme);
    if (!nearest) return null;
    if (nearest.hex !== currentHex) deferred.queue("on-brand-cell-fill-snap", () => cell.fill.setSolidColor(`#${nearest.hex}`));
    return nearest.hex;
  } catch {
    logColourSkip("on-brand-cell-fill-snap-read");
    return null;
  }
}

// A cell has 4 independent borders (unlike a shape's single lineFormat) —
// each is snapped independently, matching how replaceCellBorders already
// treats them as 4 separate colour slots rather than one.
function snapCellLineToTheme(cell: RawTableCell, theme: ThemeColors, deferred: DeferredWrites): void {
  for (const side of CELL_BORDER_SIDES) {
    try {
      const currentHex = normalizeHex(cell.borders[side].color);
      if (!currentHex) continue;
      const nearest = findNearestThemeFillColor(currentHex, theme);
      if (nearest && nearest.hex !== currentHex) {
        deferred.queue("on-brand-cell-line-snap", () => { cell.borders[side].color = `#${nearest.hex}`; });
      }
    } catch { logColourSkip("on-brand-cell-line-read"); }
  }
}

// A cell's `.font` is one cell-wide value (no per-word range the way a
// shape's textRange.getSubstring() slices words) — same limitation
// replaceCellFont works around with textRuns for word-level colour, but
// contrast-adapting after a fill snap is a single, uniform colour choice
// per cell regardless, so the cell-wide property is exactly what's needed
// here (matches adaptTextColorForNewFills's shape-level equivalent).
function adaptCellTextColorForNewFills(
  updatedCells: CellWithNewFill[],
  lightTextColor: string,
  darkTextColor: string,
  deferred: DeferredWrites,
): void {
  for (const { cell, newFillHex } of updatedCells) {
    const fontColor = pickContrastText(newFillHex, lightTextColor, darkTextColor);
    deferred.queue("cell-text-color-adapt", () => { cell.font.color = `#${fontColor}`; });
  }
}

export async function applyOnBrandFill(themeColors: ThemeColors | null): Promise<void> {
  if (!hasPpt()) return;
  const theme = themeColors ?? (await extractThemeColors());
  const lightTextColor = normalizeHex(theme.lt1) ?? "FFFFFF";
  const darkTextColor = normalizeHex(theme.dk1) ?? "000000";

  try {
    await requireSelectedShapes("On-Brand Fill");
    await requireSupportedShapes("On-Brand Fill");
    await ppt().run(async (context) => {
      const deferred = new DeferredWrites();
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      await loadFillAndLineForSnap(context, regularShapes);

      const updatedShapes: ShapeWithNewFill[] = [];
      for (const shape of regularShapes) {
        const newFillHex = snapShapeFillToTheme(shape, theme, deferred);
        if (newFillHex) updatedShapes.push({ shape, newFillHex });
        snapShapeLineToTheme(shape, theme, deferred);
      }
      await adaptTextColorForNewFills(context, updatedShapes, lightTextColor, darkTextColor, deferred);

      const cells = await collectTableCells(context, tableShapes);
      await loadCellFillAndLineForSnap(context, cells);

      const updatedCells: CellWithNewFill[] = [];
      for (const cell of cells) {
        const newFillHex = snapCellFillToTheme(cell, theme, deferred);
        if (newFillHex) updatedCells.push({ cell, newFillHex });
        snapCellLineToTheme(cell, theme, deferred);
      }
      adaptCellTextColorForNewFills(updatedCells, lightTextColor, darkTextColor, deferred);

      // Every fill/line/font write above is queued, not applied — flush them
      // all into one context.sync() so the whole action is a single native
      // undo step instead of one per intermediate sync.
      deferred.flush();
      await context.sync();
    });
  } catch (e) {
    logColourError("colour-on-brand", colourErrorType(e, "ColourOnBrandError"), e);
    throw e;
  }
}

// ── Shuffle colour ─────────────────────────────────────────────────────────────
// Mirrors DeCKorate shuffleColor: builds a differentiation-preserving color map
// so shapes with different fills each shift to a distinct next accent, rather
// than all turning the same color. Works on groups by flattening through
// getSelectedShapesFlat (which now calls flattenGroupShapes).

async function loadFillForShuffle(
  context: PowerPoint.RequestContext,
  shapes: PowerPoint.Shape[],
): Promise<void> {
  for (const shape of shapes) {
    try { shape.fill.load("type,foregroundColor"); } catch { logColourSkip("shuffle-fill-load"); }
  }
  await context.sync();
}

async function loadCellFillForShuffle(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
): Promise<void> {
  if (cells.length === 0) return;
  for (const cell of cells) {
    try { cell.fill.load("type,foregroundColor"); } catch { logColourSkip("shuffle-cell-fill-load"); }
  }
  try { await context.sync(); } catch { logColourSkip("shuffle-cell-fill-load-sync"); }
}

// Records `hex` into `seen`/`out` the first time it's encountered, skipping
// near-white — shared by every collectUniqueShuffle* collector below so the
// per-shape/per-cell loops don't each repeat the same dedup/near-white
// check inline (which is what pushed their cognitive complexity over
// SonarQube's limit: nested loops each containing their own copy of this
// logic score far higher than one loop calling this out).
function addUniqueShuffleColor(hex: string | null | undefined, seen: Set<string>, out: string[]): void {
  if (!hex || isNearWhite(hex)) return;
  if (!seen.has(hex)) { seen.add(hex); out.push(hex); }
}

function collectShapeShuffleFillColors(shapes: PowerPoint.Shape[], seen: Set<string>, out: string[]): void {
  for (const shape of shapes) {
    try {
      if (!isSolidFill(shape.fill)) continue;
      addUniqueShuffleColor(normalizeHex(shape.fill.foregroundColor)?.toUpperCase(), seen, out);
    } catch { logColourSkip("shuffle-color-collect"); }
  }
}

function collectCellShuffleFillColors(cells: RawTableCell[], seen: Set<string>, out: string[]): void {
  for (const cell of cells) {
    for (const raw of collectCellHexesForColorType(cell, "fill")) {
      addUniqueShuffleColor(raw.toUpperCase(), seen, out);
    }
  }
}

// Collects unique non-white solid fills from BOTH shapes and table cells,
// in first-seen order (DeCKorate pass 1) — shapes then cells, so a colour
// shared between a shape and a cell maps to the SAME new colour once
// buildShuffleColorMap runs, instead of the two being shuffled from
// independent, possibly-conflicting maps.
function collectUniqueShuffleColors(shapes: PowerPoint.Shape[], cells: RawTableCell[]): string[] {
  const uniqueColors: string[] = [];
  const seen = new Set<string>();
  collectShapeShuffleFillColors(shapes, seen, uniqueColors);
  collectCellShuffleFillColors(cells, seen, uniqueColors);
  return uniqueColors;
}

// Maps each unique colour to a distinct next accent (DeCKorate algorithm).
function buildShuffleColorMap(uniqueColors: string[], accents: string[]): Map<string, string> {
  const colorMap = new Map<string, string>();
  if (uniqueColors.length === 0) return colorMap;

  let startIdx = 0;
  const firstInAccents = accents.indexOf(uniqueColors[0]!);
  if (firstInAccents >= 0) startIdx = firstInAccents + 1;
  for (let i = 0; i < uniqueColors.length; i++) {
    colorMap.set(uniqueColors[i]!, accents[(startIdx + i) % accents.length]!);
  }
  return colorMap;
}

// Applies the colour map to every matching shape AND table cell (DeCKorate
// pass 2, extended to cells) — returns both lists separately since shapes
// and cells adapt their (differently-shaped) text contrast through two
// different helpers (adaptTextColorForNewFills vs adaptCellTextColorForNewFills).
function applyShuffleColorMap(
  shapes: PowerPoint.Shape[],
  cells: RawTableCell[],
  colorMap: Map<string, string>,
  deferred: DeferredWrites,
): { shuffledShapes: ShapeWithNewFill[]; shuffledCells: CellWithNewFill[] } {
  const shuffledShapes: ShapeWithNewFill[] = [];
  for (const shape of shapes) {
    try {
      if (!isSolidFill(shape.fill)) continue;
      const currentHex = normalizeHex(shape.fill.foregroundColor)?.toUpperCase();
      if (!currentHex || !colorMap.has(currentHex)) continue;
      const newFillHex = colorMap.get(currentHex)!;
      deferred.queue("shuffle-fill-apply", () => shape.fill.setSolidColor(`#${newFillHex}`));
      shuffledShapes.push({ shape, newFillHex });
    } catch { logColourSkip("shuffle-color-apply"); }
  }
  const shuffledCells: CellWithNewFill[] = [];
  for (const cell of cells) {
    try {
      if (!isSolidFill(cell.fill)) continue;
      const currentHex = normalizeHex(cell.fill.foregroundColor)?.toUpperCase();
      if (!currentHex || !colorMap.has(currentHex)) continue;
      const newFillHex = colorMap.get(currentHex)!;
      deferred.queue("shuffle-cell-fill-apply", () => cell.fill.setSolidColor(`#${newFillHex}`));
      shuffledCells.push({ cell, newFillHex });
    } catch { logColourSkip("shuffle-cell-color-apply"); }
  }
  return { shuffledShapes, shuffledCells };
}

// ── Shuffle: line target ────────────────────────────────────────────────────────
// Mirrors the fill pass above one level down (line/border colour instead of
// fill), reusing the same buildShuffleColorMap — there's no text-contrast
// step here since a border colour change doesn't affect text readability.

async function loadLineForShuffle(
  context: PowerPoint.RequestContext,
  shapes: PowerPoint.Shape[],
): Promise<void> {
  for (const shape of shapes) {
    try { shape.lineFormat.load("color,visible"); } catch { logColourSkip("shuffle-line-load"); }
  }
  await context.sync();
}

async function loadCellLineForShuffle(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
): Promise<void> {
  if (cells.length === 0) return;
  for (const cell of cells) loadCellBorderColors(cell);
  try { await context.sync(); } catch { logColourSkip("shuffle-cell-line-load-sync"); }
}

function collectShapeShuffleLineColors(shapes: PowerPoint.Shape[], seen: Set<string>, out: string[]): void {
  for (const shape of shapes) {
    try {
      if (shape.lineFormat.visible === false) continue;
      addUniqueShuffleColor(normalizeHex(shape.lineFormat.color)?.toUpperCase(), seen, out);
    } catch { logColourSkip("shuffle-line-collect"); }
  }
}

function collectCellShuffleLineColors(cells: RawTableCell[], seen: Set<string>, out: string[]): void {
  for (const cell of cells) {
    for (const raw of collectCellHexesForColorType(cell, "line")) {
      addUniqueShuffleColor(raw.toUpperCase(), seen, out);
    }
  }
}

function collectUniqueShuffleLineColors(shapes: PowerPoint.Shape[], cells: RawTableCell[]): string[] {
  const uniqueColors: string[] = [];
  const seen = new Set<string>();
  collectShapeShuffleLineColors(shapes, seen, uniqueColors);
  collectCellShuffleLineColors(cells, seen, uniqueColors);
  return uniqueColors;
}

function applyShuffleColorMapToShapeLines(shapes: PowerPoint.Shape[], colorMap: Map<string, string>, deferred: DeferredWrites): void {
  for (const shape of shapes) {
    try {
      if (shape.lineFormat.visible === false) continue;
      const currentHex = normalizeHex(shape.lineFormat.color)?.toUpperCase();
      if (!currentHex || !colorMap.has(currentHex)) continue;
      const newHex = colorMap.get(currentHex)!;
      deferred.queue("shuffle-line-apply", () => { shape.lineFormat.color = `#${newHex}`; });
    } catch { logColourSkip("shuffle-line-apply"); }
  }
}

function applyShuffleColorMapToCellLines(cells: RawTableCell[], colorMap: Map<string, string>, deferred: DeferredWrites): void {
  for (const cell of cells) {
    for (const side of CELL_BORDER_SIDES) {
      try {
        const currentHex = normalizeHex(cell.borders[side].color)?.toUpperCase();
        if (!currentHex || !colorMap.has(currentHex)) continue;
        const newHex = colorMap.get(currentHex)!;
        deferred.queue("shuffle-cell-line-apply", () => { cell.borders[side].color = `#${newHex}`; });
      } catch { logColourSkip("shuffle-cell-line-apply"); }
    }
  }
}

// A cell has 4 independent borders (unlike a shape's single lineFormat) —
// each is shuffled independently, matching how replaceCellBorders/
// snapCellLineToTheme already treat them as 4 separate colour slots.
function applyShuffleColorMapToLines(
  shapes: PowerPoint.Shape[],
  cells: RawTableCell[],
  colorMap: Map<string, string>,
  deferred: DeferredWrites,
): void {
  applyShuffleColorMapToShapeLines(shapes, colorMap, deferred);
  applyShuffleColorMapToCellLines(cells, colorMap, deferred);
}

// ── Shuffle: font target ────────────────────────────────────────────────────────
// Operates on the same per-paragraph/per-word font ranges used by detect/
// replace (see loadParagraphFontRangesForShapes) rather than the shape's
// whole textRange, so a text box with two differently-coloured words shuffles
// each word's colour independently instead of reading back a mixed/null value.

async function loadCellFontForShuffle(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
): Promise<void> {
  if (cells.length === 0) return;
  for (const cell of cells) {
    try { cell.load("textRuns"); } catch { logColourSkip("shuffle-cell-font-load"); }
  }
  try { await context.sync(); } catch { logColourSkip("shuffle-cell-font-load-sync"); }
}

function collectShapeShuffleFontColors(
  fontRangesByShape: Map<PowerPoint.Shape, PowerPoint.TextRange[]>,
  seen: Set<string>,
  out: string[],
): void {
  for (const ranges of fontRangesByShape.values()) {
    for (const range of ranges) {
      try {
        addUniqueShuffleColor(normalizeHex(range.font.color)?.toUpperCase(), seen, out);
      } catch { logColourSkip("shuffle-font-collect"); }
    }
  }
}

function collectCellShuffleFontColors(cells: RawTableCell[], seen: Set<string>, out: string[]): void {
  for (const cell of cells) {
    for (const raw of collectCellHexesForColorType(cell, "font")) {
      addUniqueShuffleColor(raw.toUpperCase(), seen, out);
    }
  }
}

function collectUniqueShuffleFontColors(
  fontRangesByShape: Map<PowerPoint.Shape, PowerPoint.TextRange[]>,
  cells: RawTableCell[],
): string[] {
  const uniqueColors: string[] = [];
  const seen = new Set<string>();
  collectShapeShuffleFontColors(fontRangesByShape, seen, uniqueColors);
  collectCellShuffleFontColors(cells, seen, uniqueColors);
  return uniqueColors;
}

function applyShuffleColorMapToFont(
  fontRangesByShape: Map<PowerPoint.Shape, PowerPoint.TextRange[]>,
  cells: RawTableCell[],
  colorMap: Map<string, string>,
  deferred: DeferredWrites,
): void {
  for (const ranges of fontRangesByShape.values()) {
    for (const range of ranges) {
      try {
        const currentHex = normalizeHex(range.font.color)?.toUpperCase();
        if (!currentHex || !colorMap.has(currentHex)) continue;
        const newHex = colorMap.get(currentHex)!;
        deferred.queue("shuffle-font-apply", () => { range.font.color = `#${newHex}`; });
      } catch { logColourSkip("shuffle-font-apply"); }
    }
  }
  // Reuses replaceCellFont's word-level textRuns rewrite verbatim — colorMap
  // is a bare-hex from→to lookup with the exact same shape replaceColours'
  // lookup already has, so shuffling a cell's font is just "replace" with a
  // differentiation-preserving map instead of a user-picked one.
  for (const cell of cells) {
    replaceCellFont(cell, colorMap, deferred);
  }
}

export async function shuffleColour(
  themeColors: ThemeColors | null,
  target: ColourTarget = "fill",
): Promise<void> {
  if (!hasPpt()) return;
  const theme = themeColors ?? (await extractThemeColors());
  const accents = getAccentHexes(theme).map((h) => h.toUpperCase());
  if (accents.length === 0) return;

  const lightTextColor = normalizeHex(theme.lt1) ?? "FFFFFF";
  const darkTextColor = normalizeHex(theme.dk1) ?? "000000";

  try {
    await requireSelectedShapes("Shuffle Colors");
    await requireSupportedShapes("Shuffle Colors");
    await ppt().run(async (context) => {
      const deferred = new DeferredWrites();
      const shapes = await getSelectedShapesFlat(context);
      const { regularShapes, tableShapes } = splitShapesAndTables(shapes);
      const cells = await collectTableCells(context, tableShapes);
      if (regularShapes.length === 0 && cells.length === 0) return;

      if (target === "line") {
        await loadLineForShuffle(context, regularShapes);
        await loadCellLineForShuffle(context, cells);
        const colorMap = buildShuffleColorMap(collectUniqueShuffleLineColors(regularShapes, cells), accents);
        applyShuffleColorMapToLines(regularShapes, cells, colorMap, deferred);
      } else if (target === "font") {
        const fontRangesByShape = await loadParagraphFontRangesForShapes(
          context,
          regularShapes.filter(supportsTextFrame),
        );
        await loadCellFontForShuffle(context, cells);
        const colorMap = buildShuffleColorMap(collectUniqueShuffleFontColors(fontRangesByShape, cells), accents);
        applyShuffleColorMapToFont(fontRangesByShape, cells, colorMap, deferred);
      } else {
        await loadFillForShuffle(context, regularShapes);
        await loadCellFillForShuffle(context, cells);
        const colorMap = buildShuffleColorMap(collectUniqueShuffleColors(regularShapes, cells), accents);
        const { shuffledShapes, shuffledCells } = applyShuffleColorMap(regularShapes, cells, colorMap, deferred);
        await adaptTextColorForNewFills(context, shuffledShapes, lightTextColor, darkTextColor, deferred);
        adaptCellTextColorForNewFills(shuffledCells, lightTextColor, darkTextColor, deferred);
      }

      // Single flush + sync so the whole shuffle lands as one native undo
      // step instead of one per intermediate sync.
      deferred.flush();
      await context.sync();
    });
  } catch (e) {
    logColourError("colour-shuffle", colourErrorType(e, "ColourShuffleError"), e);
    throw e;
  }
}

// ── Get selected shape colours ─────────────────────────────────────────────────

// One shape/cell's own load-for-`colorType` switch, split out of
// loadShapesForColorType below so its cognitive complexity is counted once
// per switch instead of accumulating across two full shape+cell loops in a
// single function (the loop and try/catch here don't nest — the switch
// itself is the top-level statement, keeping this well under the limit).
function loadShapeForColorType(shape: PowerPoint.Shape, colorType: ColourTarget): void {
  try {
    switch (colorType) {
      case "fill":
        shape.fill.load("type,foregroundColor");
        break;
      case "line":
        shape.lineFormat.load("color");
        break;
      case "font":
        // Non-text shapes (lines/connectors etc.) are intentionally left
        // unloaded rather than skipped-and-logged — touching .textFrame
        // on one would reject the whole batched sync below.
        if (supportsTextFrame(shape)) shape.textFrame.textRange.load("text");
        break;
      default:
        assertNever(colorType);
    }
  } catch { logColourSkip("selected-color-load"); }
}

function loadCellForColorType(cell: RawTableCell, colorType: ColourTarget): void {
  try {
    switch (colorType) {
      case "fill":
        cell.fill.load("type,foregroundColor");
        break;
      case "line":
        for (const side of CELL_BORDER_SIDES) cell.borders[side].load("color");
        break;
      case "font":
        // Both loaded: textRuns for genuinely per-word colour, PLUS the
        // simpler cell-wide font.color — see collectCellHexesForColorType
        // for why the latter is needed too.
        cell.load("textRuns");
        cell.font.load("color");
        break;
      default:
        assertNever(colorType);
    }
  } catch { logColourSkip("table-cell-selected-color-load"); }
}

// Loads the property relevant to `colorType` on every shape/table cell and
// syncs once. Returns false when the sync itself failed (caller returns []
// in that case).
async function loadShapesForColorType(
  context: PowerPoint.RequestContext,
  shapes: PowerPoint.Shape[],
  cells: RawTableCell[],
  colorType: ColourTarget,
): Promise<boolean> {
  for (const shape of shapes) loadShapeForColorType(shape, colorType);
  for (const cell of cells) loadCellForColorType(cell, colorType);
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("selected-color-load-sync");
    return false;
  }
}

// Reads the hex colour(s) a set of already-loaded per-paragraph font ranges
// contribute — a shape can contribute more than one hex for mixed-colour text.
function collectHexesFromFontRanges(ranges: PowerPoint.TextRange[]): string[] {
  const hexes: string[] = [];
  for (const r of ranges) {
    try {
      const h = normalizeHex(r.font.color);
      if (h) hexes.push(h);
    } catch { logColourSkip("font-range-hex-read"); }
  }
  return hexes;
}

// Reads the hex colour(s) a single shape contributes for `colorType`.
function collectShapeHexesForColorType(
  shape: PowerPoint.Shape,
  colorType: ColourTarget,
  fontRangesByShape: Map<PowerPoint.Shape, PowerPoint.TextRange[]>,
): string[] {
  try {
    switch (colorType) {
      case "font":
        return collectHexesFromFontRanges(fontRangesByShape.get(shape) ?? []);
      case "line": {
        const h = normalizeHex(shape.lineFormat.color);
        return h ? [h] : [];
      }
      case "fill": {
        if (!isSolidFill(shape.fill)) return [];
        const h = normalizeHex(shape.fill.foregroundColor);
        return h ? [h] : [];
      }
      default:
        return assertNever(colorType);
    }
  } catch {
    logColourSkip("shape-hex-read");
    return [];
  }
}

// One-per-target helpers behind collectCellHexesForColorType below — split
// out so each target's own branching/loop is counted on its own instead of
// accumulating into one switch's cognitive complexity score.

// textRuns only ever reflects a colour explicitly set PER RUN — a colour
// applied to the whole cell at once (e.g. via the "Apply Font Color" quick
// action, which writes cell.font.color directly, not textRuns) never shows
// up there at all, so a cell coloured that way read back as "no colour
// detected" no matter what. cell.font.color is the fallback for exactly
// that case; only used when textRuns found nothing; a genuinely mixed cell
// already has per-run hexes above and doesn't need it.
function collectCellFontHexes(cell: RawTableCell): string[] {
  const hexes: string[] = [];
  for (const run of cell.textRuns) {
    if (!run.text.trim()) continue;
    const h = normalizeHex(run.font?.color);
    if (h) hexes.push(h);
  }
  if (hexes.length === 0) {
    const cellHex = normalizeHex(cell.font.color);
    if (cellHex) hexes.push(cellHex);
  }
  return hexes;
}

function collectCellLineHexes(cell: RawTableCell): string[] {
  const hexes: string[] = [];
  for (const side of CELL_BORDER_SIDES) {
    const h = normalizeHex(cell.borders[side].color);
    if (h) hexes.push(h);
  }
  return hexes;
}

function collectCellFillHexes(cell: RawTableCell): string[] {
  if (!isSolidFill(cell.fill)) return [];
  const h = normalizeHex(cell.fill.foregroundColor);
  return h ? [h] : [];
}

// Reads the hex colour(s) a single table cell contributes for `colorType` —
// a table's own shape-level .fill/.lineFormat/.textFrame aren't meaningful
// (a table's colour lives per cell), which is why this was previously
// missing here entirely: getSelectedShapeColours iterated shapes only, so a
// selected table contributed nothing no matter what its cells looked like
// (first-hand different limitation from replaceColours' table handling,
// which already went through applyReplaceToTableShapes correctly).
function collectCellHexesForColorType(cell: RawTableCell, colorType: ColourTarget): string[] {
  try {
    switch (colorType) {
      case "font": return collectCellFontHexes(cell);
      case "line": return collectCellLineHexes(cell);
      case "fill": return collectCellFillHexes(cell);
      default: return assertNever(colorType);
    }
  } catch {
    logColourSkip("table-cell-hex-read");
    return [];
  }
}

// When the user has a specific word/range highlighted (not just a shape
// selected), font colour detection for "Selected Shapes" scope should offer
// just that highlighted colour — not every distinct word colour in the
// whole shape — so Replace's row list matches exactly what a live-selected
// replace (see replaceFontInSelectedTextRange) will act on. Returns null
// when nothing is highlighted, so the caller falls through to the existing
// whole-shape scan; returns [] (not null) when something IS highlighted but
// its colour can't be read as one value (e.g. the highlight itself spans
// mixed colours) — the user's highlight still takes precedence over
// silently reverting to a whole-shape scan they didn't ask for.
async function collectSelectedTextRangeColour(
  context: PowerPoint.RequestContext,
): Promise<ShapeColorEntry[] | null> {
  try {
    if (await selectionIncludesTable(context)) return null;
    const textRange = context.presentation.getSelectedTextRangeOrNullObject();
    textRange.load("isNullObject,text");
    await context.sync();
    if (textRange.isNullObject || !textRange.text) return null;
    if (!(await isGenuinePartialTextSelection(context, textRange))) return null;

    textRange.font.load("color");
    await context.sync();
    const hex = normalizeHex(textRange.font.color);
    return hex ? [{ hex, colorType: "font", count: 1, nearest: null }] : [];
  } catch {
    logColourSkip("selected-text-range-font-detect");
    return null;
  }
}

export async function getSelectedShapeColours(
  colorType: ColourTarget,
): Promise<ShapeColorEntry[]> {
  if (!hasPpt()) return [];
  try {
    return await ppt().run(async (context) => {
      if (colorType === "font") {
        const fromTextRange = await collectSelectedTextRangeColour(context);
        if (fromTextRange !== null) return fromTextRange;
      }

      const allShapes = await getSelectedShapesFlat(context);
      if (allShapes.length === 0) return [];

      // A table's colour lives per CELL — its own shape-level .fill/
      // .lineFormat/.textFrame aren't meaningful, so a selected table used
      // to silently contribute nothing here no matter what its cells
      // looked like (unlike replaceColours/detectNonBrandColours, which
      // already routed tables through their cells correctly).
      const { regularShapes, tableShapes } = splitShapesAndTables(allShapes);
      const cells = await collectTableCells(context, tableShapes);
      if (regularShapes.length === 0 && cells.length === 0) return [];

      const loaded = await loadShapesForColorType(context, regularShapes, cells, colorType);
      if (!loaded) return [];

      // Font: paragraph boundaries only exist in the plain text (see
      // splitIntoParagraphRanges) — slice them out, then load each slice's
      // colour in a second sync. Skip shapes that don't support a text frame
      // (lines/connectors etc.) — touching .textFrame on one of those would
      // reject the whole batched sync below, not just that one shape.
      const fontRangesByShape = colorType === "font"
        ? await loadParagraphFontRangesForShapes(context, regularShapes.filter(supportsTextFrame))
        : new Map<PowerPoint.Shape, PowerPoint.TextRange[]>();

      const counts = new Map<string, number>();
      for (const shape of regularShapes) {
        for (const h of collectShapeHexesForColorType(shape, colorType, fontRangesByShape)) {
          counts.set(h, (counts.get(h) ?? 0) + 1);
        }
      }
      for (const cell of cells) {
        for (const h of collectCellHexesForColorType(cell, colorType)) {
          counts.set(h, (counts.get(h) ?? 0) + 1);
        }
      }

      return [...counts.entries()]
        .sort((a, b) => b[1] - a[1])
        .map(([hex, count]) => ({ hex, colorType, count, nearest: null }));
    });
  } catch (e) {
    logColourError("colour-shapes", "ColourShapesError", e);
    return [];
  }
}

// ── Recursive group flattening ────────────────────────────────────────────────
// Mirrors colorTools.js _flattenShapesRecursive — descends into PowerPoint groups
// up to maxDepth levels so nested shapes are included in every scan/replace pass.

// Reads one group's children and queues their id/name/type load. Returns []
// (rather than throwing) on any access failure, so one bad group can't stop
// the others from being flattened.
function loadGroupChildren(grp: PowerPoint.Shape): PowerPoint.Shape[] {
  try {
    const children = [...(grp as unknown as { group: RawGroup }).group.shapes.items];
    for (const child of children) {
      (child as unknown as { load(s: string): void }).load("id,name,type");
    }
    return children;
  } catch {
    logColourSkip("group-children-read");
    return [];
  }
}

async function flattenGroupShapes(
  context: PowerPoint.RequestContext,
  shapeItems: PowerPoint.Shape[],
  depth = 0,
  maxDepth = 6,
): Promise<PowerPoint.Shape[]> {
  if (!shapeItems || shapeItems.length === 0 || depth > maxDepth) return [];
  const out: PowerPoint.Shape[] = [];
  const groups: PowerPoint.Shape[] = [];

  for (const shape of shapeItems) {
    if (shapeTypeOf(shape) === "group") groups.push(shape);
    else out.push(shape);
  }
  if (groups.length === 0) return out;

  for (const grp of groups) {
    try { (grp as unknown as { group: RawGroup }).group.shapes.load("items"); } catch { logColourSkip("group-children-load"); }
  }
  await context.sync();

  const childBatches = groups.map(loadGroupChildren);
  await context.sync();

  for (const children of childBatches) {
    out.push(...(await flattenGroupShapes(context, children, depth + 1, maxDepth))); // NOSONAR ordered Office.js operations on a shared context
  }

  return out;
}

// ── Detect non-brand colours ───────────────────────────────────────────────────

// Total slide count for the active presentation — used to cap the "Slide
// Range" from/to inputs in ReplaceColorsModal/NonBrandModal so a user can't
// enter a range that doesn't exist in the deck.
export async function getSlideCount(): Promise<number> {
  if (!hasPpt()) return 0;
  try {
    return await ppt().run(async (context) => {
      context.presentation.slides.load("items");
      await context.sync();
      return context.presentation.slides.items.length;
    });
  } catch (e) {
    logColourError("colour-slide-count", "ColourSlideCountError", e);
    return 0;
  }
}

// Mirrors DeCKorate colorTools.getCurrentSlideIndex — runs in its own PowerPoint.run
// so it doesn't burden the main detection run with 57 ID-load operations.
export async function getCurrentSlideIndex(): Promise<number> {
  let index = 0;
  if (!hasPpt()) return index;
  try {
    await ppt().run(async (context) => {
      const sel = context.presentation.getSelectedSlides();
      sel.load("items");
      const allSlides = context.presentation.slides;
      allSlides.load("items");
      await context.sync();
      if (sel.items.length > 0 && allSlides.items.length > 0) {
        for (let i = 0; i < allSlides.items.length; i++) {
          if ((allSlides.items[i] as unknown as SlideWithId).id ===
              (sel.items[0] as unknown as SlideWithId).id) {
            index = i;
            break;
          }
        }
      }
    });
  } catch (e) {
    logColourError("colour-index", "ColourIndexError", e); // default to 0
  }
  return index;
}

// Resolves the "current slide only" scope into a concrete 1-based
// slideFrom/slideTo pair, otherwise passes the caller's own range through
// unchanged — shared by detectNonBrandColours and detectColoursInScope so
// both resolve "current slide" identically instead of each re-deriving it.
async function resolveCurrentSlideOnlyRange(
  currentSlideOnly: boolean,
  slideFrom?: number,
  slideTo?: number,
): Promise<{ resolvedFrom?: number; resolvedTo?: number }> {
  if (!currentSlideOnly) return { resolvedFrom: slideFrom, resolvedTo: slideTo };
  const idx = await getCurrentSlideIndex(); // 0-based
  const oneBased = idx + 1; // convert to 1-based to match slideFrom/slideTo
  return { resolvedFrom: oneBased, resolvedTo: oneBased };
}

type NonBrandRecorder = (h: string | null | undefined, t: ColourTarget) => void;

function recordFillColor(fill: PowerPoint.ShapeFill, record: NonBrandRecorder): void {
  try {
    if (isSolidFill(fill)) record(fill.foregroundColor, "fill");
  } catch { logColourSkip("non-brand-fill-record"); }
}

// ── PASS: FILL ──
async function scanFillNonBrand(
  context: PowerPoint.RequestContext,
  regularShapes: PowerPoint.Shape[],
  cells: RawTableCell[],
  record: NonBrandRecorder,
): Promise<void> {
  await loadAndSync(context, regularShapes, (s) => s.fill.load("type,foregroundColor"));
  for (const shape of regularShapes) recordFillColor(shape.fill, record);

  if (cells.length === 0) return;
  await loadAndSync(context, cells, (c) => c.fill.load("type,foregroundColor"));
  for (const cell of cells) recordFillColor(cell.fill, record);
}

function recordLineColor(target: { color: string }, record: NonBrandRecorder): void {
  try { record(target.color, "line"); } catch { logColourSkip("non-brand-line-record"); }
}

async function loadCellBorders(context: PowerPoint.RequestContext, cells: RawTableCell[]): Promise<void> {
  for (const cell of cells) {
    for (const side of CELL_BORDER_SIDES) {
      try { cell.borders[side].load("color"); } catch { logColourSkip("table-cell-border-load"); }
    }
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("table-cell-border-sync");
  }
}

// ── PASS: LINE ──
async function scanLineNonBrand(
  context: PowerPoint.RequestContext,
  regularShapes: PowerPoint.Shape[],
  cells: RawTableCell[],
  record: NonBrandRecorder,
): Promise<void> {
  await loadAndSync(context, regularShapes, (s) => s.lineFormat.load("color"));
  for (const shape of regularShapes) recordLineColor(shape.lineFormat, record);

  if (cells.length === 0) return;
  await loadCellBorders(context, cells);
  for (const cell of cells) {
    for (const side of CELL_BORDER_SIDES) recordLineColor(cell.borders[side], record);
  }
}

// ── PASS: FONT — paragraph colours recovered via splitIntoParagraphRanges ──
async function scanFontNonBrandShapes(
  context: PowerPoint.RequestContext,
  textCapableShapes: PowerPoint.Shape[],
  record: NonBrandRecorder,
): Promise<void> {
  for (const shape of textCapableShapes) {
    try { shape.textFrame.textRange.load("text"); } catch { logColourSkip("non-brand-font-text-load"); }
  }
  try { await context.sync(); } catch { logColourSkip("non-brand-font-sync"); }

  const fontRangesByShape = await loadParagraphFontRangesForShapes(context, textCapableShapes);
  for (const shape of textCapableShapes) {
    const shapeColors = new Set<string>();
    for (const r of fontRangesByShape.get(shape) ?? []) {
      try { const h = normalizeHex(r.font.color); if (h) shapeColors.add(h); } catch { logColourSkip("non-brand-font-hex-read"); }
    }
    for (const h of shapeColors) record(h, "font");
  }
}

// Reuses collectCellHexesForColorType (the same function Replace's own
// detection and Shuffle's collector call) rather than re-deriving cell font
// colours here — it already reads textRuns for genuinely per-word colour
// AND falls back to the cell-wide font.color for a colour applied to the
// whole cell at once (e.g. via "Apply Font Color"), so this, Replace, and
// Shuffle all agree on exactly what's in a cell instead of drifting apart.
async function scanFontNonBrandCells(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
  record: NonBrandRecorder,
): Promise<void> {
  if (cells.length === 0) return;
  await loadAndSync(context, cells, (c) => { c.load("textRuns"); c.font.load("color"); });
  for (const cell of cells) {
    for (const h of collectCellHexesForColorType(cell, "font")) record(h, "font");
  }
}

async function scanFontNonBrand(
  context: PowerPoint.RequestContext,
  regularShapes: PowerPoint.Shape[],
  cells: RawTableCell[],
  record: NonBrandRecorder,
): Promise<void> {
  // Skip shapes without a text frame (lines/connectors etc.) — one of
  // those in the batch would reject the whole sync below, not just itself.
  await scanFontNonBrandShapes(context, regularShapes.filter(supportsTextFrame), record);
  await scanFontNonBrandCells(context, cells, record);
}

// Runs the fill/line/font detection pass (for the given target) against one
// already-flattened shape list — either a single slide's shapes, or the
// current selection — and reports hits via `record`. Shared by the per-slide
// loop and the "Selected Shapes" scope so both scan identically.
async function scanShapesForNonBrand(
  context: PowerPoint.RequestContext,
  allShapes: PowerPoint.Shape[],
  target: ColourTarget,
  record: NonBrandRecorder,
): Promise<void> {
  const { regularShapes, tableShapes } = splitShapesAndTables(allShapes);
  const cells = await collectTableCells(context, tableShapes);

  switch (target) {
    case "fill":
      await scanFillNonBrand(context, regularShapes, cells, record);
      break;
    case "line":
      await scanLineNonBrand(context, regularShapes, cells, record);
      break;
    case "font":
      await scanFontNonBrand(context, regularShapes, cells, record);
      break;
    default:
      assertNever(target);
  }
}

// Walks slides [fromIdx, toIdx] (inclusive, already resolved to valid bounds
// by the caller), loading + flattening each slide's shapes and running the
// scan pass against them — shared by detectNonBrandColours and
// detectColoursInScope so both walk a slide range identically.
async function scanSlidesInRange(
  context: PowerPoint.RequestContext,
  slides: PowerPoint.Slide[],
  fromIdx: number,
  toIdx: number,
  target: ColourTarget,
  record: NonBrandRecorder,
  onProgress?: (percent: number) => void,
): Promise<void> {
  for (let si = fromIdx; si <= toIdx; si++) {
    const slide = slides[si];
    if (!slide) continue;
    try {
      slide.shapes.load("items");
      await context.sync(); // NOSONAR ordered Office.js operations on a shared context
    } catch {
      logColourSkip("scan-slide-shapes-load-sync");
      continue;
    }

    const allShapes = await loadAndFlattenShapes(context, slide.shapes.items); // NOSONAR ordered Office.js operations on a shared context
    if (!allShapes) continue;
    await scanShapesForNonBrand(context, allShapes, target, record); // NOSONAR ordered Office.js operations on a shared context

    onProgress?.(Math.round(((si - fromIdx + 1) / (toIdx - fromIdx + 1)) * 100));
  }
}

export async function detectNonBrandColours(
  themeColors: ThemeColors | null,
  options: DetectNonBrandOptions = {},
): Promise<NonBrandResult[]> {
  const {
    slideFrom,
    slideTo,
    threshold = 30,
    onProgress,
    currentSlideOnly = false,
    target = "fill",
    selectedShapesOnly = false,
  } = options;

  if (!hasPpt()) return [];
  const theme = themeColors ?? (await extractThemeColors());
  const themeHexes = new Set(
    Object.values(theme).map((h) => normalizeHex(h) ?? "").filter(Boolean),
  );

  const counts = new Map<string, Map<ColourTarget, number>>();
  const record: NonBrandRecorder = (h, t) => {
    const key = normalizeHex(h);
    if (!key) return;
    if (themeHexes.has(key)) return;
    const entry = counts.get(key) ?? new Map<ColourTarget, number>();
    entry.set(t, (entry.get(t) ?? 0) + 1);
    counts.set(key, entry);
  };

  const buildResults = (): NonBrandResult[] => {
    const results: NonBrandResult[] = [];
    for (const [hex, typeMap] of counts) {
      const nearest = findNearestThemeColor(hex, theme);
      if (nearest && nearest.distance <= threshold) continue;
      // One result per (hex, colorType) so the UI can show and fix each independently
      for (const [colorType, count] of typeMap) {
        results.push({ hex, nearest, count, colorType });
      }
    }
    return results.sort((a, b) => b.count - a.count);
  };

  // Restrict the scan to whatever shapes are currently selected in
  // PowerPoint, regardless of which slide(s) they're on.
  if (selectedShapesOnly) {
    try {
      return await ppt().run(async (context) => {
        const allShapes = await getSelectedShapesFlat(context);
        await scanShapesForNonBrand(context, allShapes, target, record);
        onProgress?.(100);
        return buildResults();
      });
    } catch (e) {
      logColourError("colour-detect", "ColourDetectError", e);
      return [];
    }
  }

  // Pre-resolve current slide in its own run (matches DeCKorate's getCurrentSlideIndex
  // pattern). Avoids falling back to scanning all slides when ID matching fails inside
  // a large shared context.
  const { resolvedFrom, resolvedTo } = await resolveCurrentSlideOnlyRange(currentSlideOnly, slideFrom, slideTo);

  try {
    return await ppt().run(async (context) => {
      const presentation = context.presentation;
      presentation.slides.load("items");
      await context.sync();

      const slides = presentation.slides.items;
      const total = slides.length;
      const fromIdx = resolvedFrom != null ? Math.max(0, resolvedFrom - 1) : 0;
      const toIdx = resolvedTo != null ? Math.min(total - 1, resolvedTo - 1) : total - 1;

      await scanSlidesInRange(context, slides, fromIdx, toIdx, target, record, onProgress);

      return buildResults();
    });
  } catch (e) {
    logColourError("colour-detect", "ColourDetectError", e);
    return [];
  }
}

// Detects every distinct colour of `target` present within a scope (current
// slide / slide range / all slides / current selection) — unlike
// detectNonBrandColours, nothing is excluded by theme distance; every
// colour found is returned as-is. Used by ReplaceColorsModal so "Colors
// from selected shapes" (and what "Replace all" targets) reflects whichever
// scope the user picked for the replace itself, not just the literal
// PowerPoint selection — those could silently disagree (e.g. scope = "Slide
// Range 1–2" but the panel still only showed colours from whatever was
// literally selected, so "Replace all" never touched slide 1's colours
// because they'd never made it into the from→to mapping to begin with).
export async function detectColoursInScope(
  target: ColourTarget,
  options: {
    slideFrom?: number;
    slideTo?: number;
    onProgress?: (percent: number) => void;
    currentSlideOnly?: boolean;
    selectedShapesOnly?: boolean;
  } = {},
): Promise<ShapeColorEntry[]> {
  const { slideFrom, slideTo, onProgress, currentSlideOnly = false, selectedShapesOnly = false } = options;
  if (!hasPpt()) return [];

  const counts = new Map<string, number>();
  const record: NonBrandRecorder = (h, t) => {
    if (t !== target) return;
    const key = normalizeHex(h);
    if (!key) return;
    counts.set(key, (counts.get(key) ?? 0) + 1);
  };
  const buildScopeResults = (): ShapeColorEntry[] =>
    [...counts.entries()]
      .sort((a, b) => b[1] - a[1])
      .map(([hex, count]) => ({ hex, colorType: target, count, nearest: null }));

  if (selectedShapesOnly) {
    try {
      return await ppt().run(async (context) => {
        const allShapes = await getSelectedShapesFlat(context);
        await scanShapesForNonBrand(context, allShapes, target, record);
        onProgress?.(100);
        return buildScopeResults();
      });
    } catch (e) {
      logColourError("colour-detect-scope", "ColourDetectScopeError", e);
      return [];
    }
  }

  const { resolvedFrom, resolvedTo } = await resolveCurrentSlideOnlyRange(currentSlideOnly, slideFrom, slideTo);

  try {
    return await ppt().run(async (context) => {
      const presentation = context.presentation;
      presentation.slides.load("items");
      await context.sync();

      const slides = presentation.slides.items;
      const total = slides.length;
      const fromIdx = resolvedFrom != null ? Math.max(0, resolvedFrom - 1) : 0;
      const toIdx = resolvedTo != null ? Math.min(total - 1, resolvedTo - 1) : total - 1;

      await scanSlidesInRange(context, slides, fromIdx, toIdx, target, record, onProgress);

      return buildScopeResults();
    });
  } catch (e) {
    logColourError("colour-detect-scope", "ColourDetectScopeError", e);
    return [];
  }
}

// ── Replace colours ────────────────────────────────────────────────────────────
// Full port of reference replaceColors():
//  • Recursive group flattening (up to 6 levels)
//  • Separate regular-shape and table passes
//  • Table: getCellOrNullObject(r,c), cell font (para-level + fallback), cell borders
//  • Fill: attempts replacement even when fill.type is uncertain (special shapes)
//  • Font: para-level first, shape-level fallback
//  • currentSlideOnly: resolves active slide via getSelectedSlides() ID matching
//  • onProgress: per-slide callback for progress reporting

// Loads the properties needed for every active target on regular shapes, in
// one batched sync. Returns false when the sync itself failed.
async function loadRegularShapesForReplace(
  context: PowerPoint.RequestContext,
  regularShapes: PowerPoint.Shape[],
  textCapableShapes: PowerPoint.Shape[],
  targets: Set<ColourTarget>,
): Promise<boolean> {
  for (const shape of regularShapes) {
    if (targets.has("fill")) {
      try { shape.fill.load("type,foregroundColor"); } catch { logColourSkip("replace-fill-load"); }
    }
    if (targets.has("line")) {
      try { shape.lineFormat.load("color"); } catch { logColourSkip("replace-line-load"); }
    }
  }
  // Font text loads are restricted to text-capable shapes — a line/
  // connector's `.textFrame` throws only when context.sync() executes,
  // which would reject this WHOLE batched sync (not just that shape) and
  // silently skip everything for every target, not just font.
  for (const shape of textCapableShapes) {
    try { shape.textFrame.textRange.load("text"); } catch { logColourSkip("replace-font-text-load"); }
  }
  try {
    await context.sync();
    return true;
  } catch {
    logColourSkip("replace-shapes-load-sync");
    return false;
  }
}

function replaceShapeFill(shape: PowerPoint.Shape, lookup: Map<string, string>, deferred: DeferredWrites): boolean {
  try {
    const hex = normalizeHex(shape.fill.foregroundColor);
    if (hex && lookup.has(hex)) {
      const newColor = `#${lookup.get(hex)}`;
      deferred.queue("replace-shape-fill", () => shape.fill.setSolidColor(newColor));
      return true;
    }
  } catch { logColourSkip("replace-shape-fill-read"); }
  return false;
}

function replaceLineColor(target: { color: string }, lookup: Map<string, string>, deferred: DeferredWrites): boolean {
  try {
    const hex = normalizeHex(target.color);
    if (hex && lookup.has(hex)) {
      const newColor = `#${lookup.get(hex)}`;
      deferred.queue("replace-line-color", () => { target.color = newColor; });
      return true;
    }
  } catch { logColourSkip("replace-line-color-read"); }
  return false;
}

// Replaces matching colours across a set of already-loaded per-paragraph
// font ranges. Shared by the regular-shape and table-cell font passes.
function replaceParagraphRanges(ranges: PowerPoint.TextRange[], lookup: Map<string, string>, deferred: DeferredWrites): number {
  let count = 0;
  for (const r of ranges) {
    try {
      const hex = normalizeHex(r.font.color);
      if (hex && lookup.has(hex)) {
        const newColor = `#${lookup.get(hex)}`;
        deferred.queue("replace-paragraph-font", () => { r.font.color = newColor; });
        count++;
      }
    } catch { logColourSkip("replace-paragraph-font-read"); }
  }
  return count;
}

// Fill — attempt even when fill.type is uncertain (some special shapes
// report an empty type but still accept setSolidColor). Font — per-
// paragraph ranges so mixed-colour text only replaces the matching
// paragraphs, leaving other colours in the same shape intact.
function applyReplaceToRegularShapes(
  regularShapes: PowerPoint.Shape[],
  targets: Set<ColourTarget>,
  lookup: Map<string, string>,
  fontRangesByShape: Map<PowerPoint.Shape, PowerPoint.TextRange[]>,
  deferred: DeferredWrites,
): number {
  let total = 0;
  for (const shape of regularShapes) {
    if (targets.has("fill") && replaceShapeFill(shape, lookup, deferred)) total++;
    if (targets.has("line") && replaceLineColor(shape.lineFormat, lookup, deferred)) total++;
    if (targets.has("font")) total += replaceParagraphRanges(fontRangesByShape.get(shape) ?? [], lookup, deferred);
  }
  return total;
}

async function replaceTableShapeLines(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
  lookup: Map<string, string>,
  deferred: DeferredWrites,
): Promise<number> {
  await loadAndSync(context, tableShapes, (s) => s.lineFormat.load("color"));

  let total = 0;
  for (const shape of tableShapes) {
    if (replaceLineColor(shape.lineFormat, lookup, deferred)) total++;
  }
  return total;
}

async function loadCellsForReplace(
  context: PowerPoint.RequestContext,
  cells: RawTableCell[],
  targets: Set<ColourTarget>,
): Promise<void> {
  for (const cell of cells) {
    loadCellFillAndFont(cell, targets);
    if (targets.has("line")) loadCellBorderColors(cell);
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("table-cell-replace-load-sync");
  }
}

function loadCellFillAndFont(cell: RawTableCell, targets: Set<ColourTarget>): void {
  if (targets.has("fill")) {
    try { cell.fill.load("type,foregroundColor"); } catch { logColourSkip("table-cell-fill-load"); }
  }
  if (targets.has("font")) {
    // textRuns for word-level replacement (see replaceCellFont), PLUS the
    // cell-wide font.color fallback for a colour that was applied to the
    // whole cell at once rather than per-run (see replaceCellFont's use of it).
    try { cell.load("textRuns"); } catch { logColourSkip("table-cell-font-load"); }
    try { cell.font.load("color"); } catch { logColourSkip("table-cell-font-load"); }
  }
}

function loadCellBorderColors(cell: RawTableCell): void {
  for (const side of CELL_BORDER_SIDES) {
    try { cell.borders[side].load("color"); } catch { logColourSkip("table-cell-border-load"); }
  }
}

function replaceCellFill(cell: RawTableCell, lookup: Map<string, string>, deferred: DeferredWrites): boolean {
  try {
    if (isSolidFill(cell.fill)) {
      const hex = normalizeHex(cell.fill.foregroundColor);
      if (hex && lookup.has(hex)) {
        const newColor = `#${lookup.get(hex)}`;
        deferred.queue("replace-cell-fill", () => cell.fill.setSolidColor(newColor));
        return true;
      }
    }
  } catch {
    logColourSkip("table-cell-fill-replace");
  }
  return false;
}

// Splits one loaded run's text into word/whitespace segments — same word
// boundary (/\S+/g) as splitIntoParagraphRanges' shape-side slicing, but
// whitespace segments must also be re-emitted here (dropped there, since
// getSubstring() only ever needed to address the words themselves): a
// cell's textRuns is written back as one complete array, so silently
// losing the whitespace runs would collapse "Red Blue" to "RedBlue". Each
// segment inherits the source run's font untouched except a matching
// word's `.color`, which is overridden on a shallow copy — the original
// run object itself is never mutated.
function splitCellRunForFontReplace(
  run: RawTextRun,
  lookup: Map<string, string>,
): { runs: RawTextRun[]; changed: number } {
  const segments = run.text.match(/\S+|\s+/g) ?? [];
  const runs: RawTextRun[] = [];
  let changed = 0;
  for (const segment of segments) {
    const hex = /\S/.test(segment) ? normalizeHex(run.font?.color) : null;
    if (hex && lookup.has(hex)) {
      runs.push({ text: segment, font: { ...run.font, color: `#${lookup.get(hex)}` } });
      changed++;
    } else {
      runs.push({ text: segment, font: run.font });
    }
  }
  return { runs, changed };
}

// Word-level replacement via `TableCell.textRuns` (PowerPointApi 1.8) — a
// settable array of `{ text, font }` records, unlike a shape's `.font`,
// which is one cell-wide value with no equivalent to a shape's per-word
// TextRange slicing. Every existing run is re-split into word/whitespace
// segments regardless of how PowerPoint had them grouped, so two adjacent,
// differently-coloured words that happened to already share one run still
// get independent treatment. The whole rebuilt array is queued as a single
// write — there's no way to address one run in isolation the way a live
// TextRange slice can be.
function replaceCellFont(cell: RawTableCell, lookup: Map<string, string>, deferred: DeferredWrites): number {
  try {
    const newRuns: RawTextRun[] = [];
    let changed = 0;
    for (const run of cell.textRuns) {
      const { runs, changed: runChanged } = splitCellRunForFontReplace(run, lookup);
      newRuns.push(...runs);
      changed += runChanged;
    }
    if (changed > 0) {
      deferred.queue("replace-cell-font", () => { cell.textRuns = newRuns; });
      return changed;
    }
    // No per-run colour matched — the cell may have been coloured as a
    // whole instead (e.g. via "Apply Font Color", which writes cell.font.color
    // directly, not textRuns; see collectCellHexesForColorType's matching
    // fallback on the detect side). Match against that instead so a colour
    // detected this way can actually be replaced, not just listed.
    const cellHex = normalizeHex(cell.font.color);
    if (cellHex && lookup.has(cellHex)) {
      const newColor = `#${lookup.get(cellHex)}`;
      deferred.queue("replace-cell-font-whole", () => {
        cell.font.color = newColor;
        // Belt-and-braces: also force every existing run to the new colour.
        // cell.font.color alone is enough when textRuns is genuinely empty,
        // but if the host actually has run-level entries whose own colour
        // just failed to parse/match above, PowerPoint renders those runs'
        // own colour over the cell-wide default — leaving the write above
        // silently invisible. Rewriting textRuns too covers either case.
        if (cell.textRuns.length > 0) {
          cell.textRuns = cell.textRuns.map((r) => ({ ...r, font: { ...r.font, color: newColor } }));
        }
      });
      return 1;
    }
    return 0;
  } catch {
    logColourSkip("table-cell-font-replace");
    return 0;
  }
}

function replaceCellBorders(cell: RawTableCell, lookup: Map<string, string>, deferred: DeferredWrites): number {
  let count = 0;
  for (const side of CELL_BORDER_SIDES) {
    if (replaceLineColor(cell.borders[side], lookup, deferred)) count++;
  }
  return count;
}

function applyReplaceToCells(
  cells: RawTableCell[],
  targets: Set<ColourTarget>,
  lookup: Map<string, string>,
  deferred: DeferredWrites,
): number {
  let total = 0;
  for (const cell of cells) {
    if (targets.has("fill") && replaceCellFill(cell, lookup, deferred)) total++;
    if (targets.has("font")) total += replaceCellFont(cell, lookup, deferred);
    if (targets.has("line")) total += replaceCellBorders(cell, lookup, deferred);
  }
  return total;
}

async function applyReplaceToTableShapes(
  context: PowerPoint.RequestContext,
  tableShapes: PowerPoint.Shape[],
  targets: Set<ColourTarget>,
  lookup: Map<string, string>,
  deferred: DeferredWrites,
): Promise<number> {
  if (tableShapes.length === 0) return 0;

  let total = targets.has("line")
    ? await replaceTableShapeLines(context, tableShapes, lookup, deferred)
    : 0;

  const cells = await collectTableCells(context, tableShapes);
  if (cells.length === 0) return total;

  await loadCellsForReplace(context, cells, targets);
  total += applyReplaceToCells(cells, targets, lookup, deferred);
  return total;
}

// Applies the from→to lookup to one already-flattened list of shapes (a
// slide's shapes, or the current selection) and returns how many instances
// were replaced. Extracted so "Selected Shapes" scope can reuse the exact
// same load/apply logic as the per-slide loop without duplicating it.
async function applyReplaceToShapeList(
  context: PowerPoint.RequestContext,
  allShapes: PowerPoint.Shape[],
  targets: Set<ColourTarget>,
  lookup: Map<string, string>,
  deferred: DeferredWrites,
): Promise<number> {
  const { regularShapes, tableShapes } = splitShapesAndTables(allShapes);
  const textCapableShapes = targets.has("font") ? regularShapes.filter(supportsTextFrame) : [];

  const loaded = await loadRegularShapesForReplace(context, regularShapes, textCapableShapes, targets);
  if (!loaded) return 0;

  // Font: paragraph boundaries only exist in the plain text — slice them
  // out with getSubstring() and load each slice's colour in a second sync.
  const fontRangesByShape = targets.has("font")
    ? await loadParagraphFontRangesForShapes(context, textCapableShapes)
    : new Map<PowerPoint.Shape, PowerPoint.TextRange[]>();

  let total = applyReplaceToRegularShapes(regularShapes, targets, lookup, fontRangesByShape, deferred);
  total += await applyReplaceToTableShapes(context, tableShapes, targets, lookup, deferred);
  return total;
}

/** A slide index range (both ends inclusive), shared by resolveCurrentSlideRange
 * and replaceAcrossSlides so the pair doesn't need to travel as two separate
 * parameters. */
interface SlideRange {
  fromIdx: number;
  toIdx: number;
}

// Resolves the active slide's index range via getSelectedSlides() ID
// matching, for the currentSlideOnly scope. Returns null (fall back to the
// caller's existing range) when nothing is selected or matching fails.
async function resolveCurrentSlideRange(
  context: PowerPoint.RequestContext,
  allSlides: PowerPoint.Slide[],
): Promise<SlideRange | null> {
  try {
    const sel = context.presentation.getSelectedSlides();
    sel.load("items");
    await context.sync();
    if (sel.items.length === 0) return null;

    const active = sel.items[0]!;
    (active as unknown as SlideWithId).load("id");
    for (const s of allSlides) (s as unknown as SlideWithId).load("id");
    await context.sync();

    const activeId = (active as unknown as SlideWithId).id;
    for (let i = 0; i < allSlides.length; i++) {
      if ((allSlides[i] as unknown as SlideWithId).id === activeId) {
        return { fromIdx: i, toIdx: i };
      }
    }
    return null;
  } catch {
    logColourSkip("current-slide-range-resolve");
    return null;
  }
}

async function replaceAcrossSlides(
  context: PowerPoint.RequestContext,
  allSlides: PowerPoint.Slide[],
  range: SlideRange,
  targets: Set<ColourTarget>,
  lookup: Map<string, string>,
  deferred: DeferredWrites,
  onProgress?: (current: number, total: number) => void,
): Promise<number> {
  const { fromIdx, toIdx } = range;
  let total = 0;
  const slideCount = toIdx - fromIdx + 1;

  for (let si = fromIdx; si <= toIdx; si++) {
    onProgress?.(si - fromIdx + 1, slideCount);

    const slide = allSlides[si];
    if (!slide) continue;
    try {
      slide.shapes.load("items");
      await context.sync(); // NOSONAR ordered Office.js operations on a shared context
    } catch {
      // Skipping this slide does not lose any earlier slide's queued
      // replacements — `deferred.flush()` only runs once, in
      // replaceColours' own try block, after this whole loop returns.
      logColourSkip("replace-slide-shapes-load-sync");
      continue;
    }

    const allShapes = await loadAndFlattenShapes(context, slide.shapes.items); // NOSONAR ordered Office.js operations on a shared context
    if (!allShapes) continue;
    total += await applyReplaceToShapeList(context, allShapes, targets, lookup, deferred); // NOSONAR ordered Office.js operations on a shared context
  }
  return total;
}

// Mirrors collectSelectedTextRangeColour's rationale, on the write side:
// when the user has a specific word/range highlighted, Replace's font
// target should act on exactly that highlighted text, not every matching
// word across the whole shape — Office.js's shape selection has no notion
// of "which word" at all. Returns null when nothing is highlighted (caller
// falls through to the normal whole-shape/whole-cell word-level replace);
// otherwise returns the count replaced here (0 or 1) — deliberately NOT
// falling through to the whole-shape path even when the highlighted text's
// colour doesn't match the mapping, since the user's highlight is a
// precise, intentional scope, not a hint to search more broadly.
async function replaceFontInSelectedTextRange(
  context: PowerPoint.RequestContext,
  lookup: Map<string, string>,
  deferred: DeferredWrites,
): Promise<number | null> {
  try {
    if (await selectionIncludesTable(context)) return null;
    const textRange = context.presentation.getSelectedTextRangeOrNullObject();
    textRange.load("isNullObject,text");
    await context.sync();
    if (textRange.isNullObject || !textRange.text) return null;
    if (!(await isGenuinePartialTextSelection(context, textRange))) return null;

    textRange.font.load("color");
    await context.sync();
    const hex = normalizeHex(textRange.font.color);
    if (hex && lookup.has(hex)) {
      const newColor = `#${lookup.get(hex)}`;
      deferred.queue("replace-selected-text-range-font", () => { textRange.font.color = newColor; });
      return 1;
    }
    return 0;
  } catch {
    logColourSkip("replace-selected-text-range-font");
    return null;
  }
}

export async function replaceColours(
  mappings: Array<{ from: string; to: string }>,
  targets: Set<ColourTarget>,
  slideFrom?: number,
  slideTo?: number,
  onProgress?: (current: number, total: number) => void,
  currentSlideOnly = false,
  selectedShapesOnly = false,
): Promise<number> {
  if (!hasPpt() || mappings.length === 0) return 0;

  const lookup = new Map<string, string>();
  for (const { from, to } of mappings) {
    const f = normalizeHex(from);
    const t = normalizeHex(to);
    if (f && t) lookup.set(f, t);
  }
  if (lookup.size === 0) return 0;

  let total = 0;

  try {
    await ppt().run(async (context) => {
      // Every fill/line/font write anywhere below is queued here rather
      // than applied+synced per shape-list/slide, so a replace spanning
      // many slides still lands as one native undo step.
      const deferred = new DeferredWrites();

      if (selectedShapesOnly) {
        const allShapes = await getSelectedShapesFlat(context);
        // Unlike Current Slide/Range/All Slides (which resolve their own
        // target slides regardless of selection), this scope is literally
        // "whatever's selected" — nothing selected means nothing to
        // replace, so this fails closed with a targeted message instead of
        // silently reporting "Replaced 0 instance(s)." with no explanation.
        if (allShapes.length === 0) throw new ColourReplaceSelectionRequiredError();
        onProgress?.(1, 1);

        // A live text-range highlight takes over the font target
        // specifically (see replaceFontInSelectedTextRange) — fill/line
        // still run through the normal whole-shape/whole-cell path below
        // regardless, since neither has a "which word" concept at all.
        let remainingTargets = targets;
        if (targets.has("font")) {
          const textRangeCount = await replaceFontInSelectedTextRange(context, lookup, deferred);
          if (textRangeCount !== null) {
            total += textRangeCount;
            remainingTargets = new Set([...targets].filter((t) => t !== "font"));
          }
        }

        total += await applyReplaceToShapeList(context, allShapes, remainingTargets, lookup, deferred);
        // Writes were only queued above — a queued write's success isn't
        // known until flush() actually runs it, so back out any failures
        // from the optimistic count instead of over-reporting.
        total -= deferred.flush();
        await context.sync();
        return;
      }

      context.presentation.slides.load("items");
      await context.sync();

      const allSlides = context.presentation.slides.items;
      let fromIdx = slideFrom != null ? Math.max(0, slideFrom - 1) : 0;
      let toIdx = slideTo != null ? Math.min(allSlides.length - 1, slideTo - 1) : allSlides.length - 1;

      // Resolve active slide when scope = "current"
      if (currentSlideOnly) {
        const range = await resolveCurrentSlideRange(context, allSlides);
        if (range) ({ fromIdx, toIdx } = range);
      }

      total += await replaceAcrossSlides(
        context,
        allSlides,
        { fromIdx, toIdx },
        targets,
        lookup,
        deferred,
        onProgress,
      );

      // Same "queued count is optimistic" correction as the selectedShapesOnly
      // branch above.
      total -= deferred.flush();
      await context.sync();
    });
  } catch (e) {
    // Every other failure mode here is swallowed after logging (see the
    // returned `total` below) — a genuinely unexpected mid-batch Office.js
    // error still degrades to "replaced what we could" rather than
    // crashing the whole operation. The missing-selection guard above is
    // the one exception: it's a message the user needs to see and act on,
    // not something to silently report as "0 replaced", so it's rethrown
    // instead of swallowed.
    if (e instanceof ColourReplaceSelectionRequiredError) {
      logColourError("colour-replace", e.name, e);
      throw e;
    }
    logColourError("colour-replace", "ColourReplaceError", e);
  }

  return total;
}

// ── Clean up styling ───────────────────────────────────────────────────────────

// Selected slides, or every slide when nothing is selected.
async function resolveCleanupSlides(context: PowerPoint.RequestContext): Promise<PowerPoint.Slide[]> {
  const slides = context.presentation.getSelectedSlides();
  slides.load("items");
  await context.sync();
  if (slides.items.length > 0) return slides.items;

  context.presentation.slides.load("items");
  await context.sync();
  return context.presentation.slides.items;
}

// Resets font colour to inherited on every text-capable shape on one slide.
async function cleanUpSlide(
  context: PowerPoint.RequestContext,
  slide: PowerPoint.Slide,
  deferred: DeferredWrites,
): Promise<number> {
  slide.shapes.load("items");
  try {
    await context.sync();
  } catch {
    logColourSkip("cleanup-slide-shapes-load-sync");
    return 0;
  }

  // Load type so non-text shapes (lines/connectors etc.) can be filtered
  // out — touching .textFrame on one of those rejects the WHOLE sync
  // below, not just that shape, and would zero out shapesFixed entirely.
  for (const shape of slide.shapes.items) {
    try { (shape as unknown as { load(s: string): void }).load("type"); } catch { logColourSkip("cleanup-shape-type-load"); }
  }
  try {
    await context.sync();
  } catch {
    logColourSkip("cleanup-shape-type-load-sync");
    return 0;
  }

  let fixed = 0;
  for (const shape of slide.shapes.items.filter(supportsTextFrame)) {
    // Setting to empty string resets to inherited value in Office.js —
    // this is a write, so it applies uniformly across the whole range
    // even where per-paragraph colours previously differed.
    deferred.queue("cleanup-font-reset", () => { shape.textFrame.textRange.font.color = ""; });
    fixed++;
  }
  return fixed;
}

export async function cleanUpStyling(): Promise<CleanUpResult> {
  if (!hasPpt()) return { shapesFixed: 0, message: "Not in PowerPoint." };
  let shapesFixed = 0;

  try {
    await ppt().run(async (context) => {
      // Queue every slide's font-reset and flush once so cleaning up N
      // slides is one native undo step instead of N.
      const deferred = new DeferredWrites();
      const targetSlides = await resolveCleanupSlides(context);
      for (const slide of targetSlides) {
        shapesFixed += await cleanUpSlide(context, slide, deferred); // NOSONAR ordered Office.js operations on a shared context
      }
      // shapesFixed above counts queued writes, not confirmed ones — a
      // queued write's success isn't known until flush() runs it, so back
      // out any failures instead of over-reporting.
      shapesFixed -= deferred.flush();
      await context.sync();
    });
  } catch (e) {
    logColourError("colour-cleanup", "ColourCleanupError", e);
  }

  return {
    shapesFixed,
    message: `Cleaned up ${shapesFixed} shape(s).`,
  };
}
